import React from 'react';
import { Button, Card, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row } from 'reactstrap';
import styles from './style.module.scss';
import ActionCard from '../../components/Footer/ActionCard';
import MoreIcon from '../../assets/images/icons/more.svg';
import EditIcon from '../../assets/images/icons/edit.svg';
import Tooltip from '../../components/Common/tooltip/tooltip';
import SKUAssignmentModal from '../../components/Common/Modals/SKUAssignmentModal';
import SuccessModal from '../../components/Common/Modals/SuccessModal';
import ColorLegendModal from '../../components/Common/Modals/ColorLegendModal';
import {
    ExportPlanRR,
    ExportWeightMeasure,
    GetHistoricSelection,
    GetRunRatePlanData,
    IExportPlanRRPayload,
    IMeasureSequencePayload,
    IMeasureVisibilityPayload,
    ImportPlanRR,
    ImportWeightMeasure,
    INPICopyPredecessorPayload,
    ISaveAndFreezePayload,
    ISkuSequencePayload,
    NPICopyPredecessorSku,
    SaveAndFreezePlan,
    SaveHistoricSelection,
    SaveRunRatePlanMeasureSequenceAndWidth,
    SaveRunRatePlanSkuSequence,
    UpdateRunRatePlanMeasureVisibility
} from '../../api-services/run-rate-plan.api';
import { GetPlanId } from '../../helpers/Helpers';
import InitialMessage from "../../components/Common/initial-message/initial-message";
import PageLoader from "../../components/Common/loader/PageLoader";
import { weekValue } from '../RollingPlan/planning-table/helper';

interface Props {
    historicRRWeeks: number;
    history: any;
    isCallRequired?: boolean;
    onApiCallComplete?: () => void;
    onLoadEnd?: () => void;
}

interface State {
    isAutoLoad: boolean;
    expandedQuarters: { [key: string]: boolean };
    selectedHistoricWeeks: number;
    selectedHisticViewType?: string;
    oldHistoricViewTypeSelection?: string;
    rows: any[];
    draggedRowIndex: number | null;
    originalRows: any[];
    hasSequenceChanged: boolean;
    isMoreMenuOpened: boolean;
    isExportMenuOpened: boolean;
    isImportMenuOpened: boolean;
    isEditable: boolean;
    editedRows: any[];
    isAttributesExpanded: boolean;
    isSKUAssignmentModalOpen: boolean;
    selectedRowForAssignment: number | null;
    // sortKey: string; // Commented out - sorting functionality disabled
    // sortDirection: string; // Commented out - sorting functionality disabled
    columnFilters: { [key: string]: { value: string; condition: string } };
    columnWidths: { [key: string]: number };
    draggedColumn: { key: string; iniMouse: number; iniSize: number } | null;
    attributeColumns: any[];
    draggedAttributeColumn: number | null;
    isLoading: boolean;
    originalDataSource: any;
    quarters: any[];
    cols: any[];
    isVisibilityMenuOpened: boolean;
    measureVisibilityList: {
        key: string;
        header: string;
        isVisible: boolean;
        prevCheck: boolean;
    }[];
    hasUnsavedEdits: boolean;
    isSuccessModalOpen: boolean;
    successModalMessage: string;
    isColorLegendModalOpen: boolean;
}

export default class RunRatePlanTable extends React.Component<Props, State> {
    constructor(props) {
        super(props);

        // Initialize with empty/default values - will be populated from API
        this.state = {
            isAutoLoad: true,
            expandedQuarters: {},
            selectedHistoricWeeks: 6,
            selectedHisticViewType: 'RR only',
            oldHistoricViewTypeSelection: 'RR only',
            rows: [],
            draggedRowIndex: null,
            originalRows: [],
            hasSequenceChanged: false,
            isMoreMenuOpened: false,
            isExportMenuOpened: false,
            isImportMenuOpened: false,
            isEditable: false,
            editedRows: [],
            isAttributesExpanded: true,
            isSKUAssignmentModalOpen: false,
            selectedRowForAssignment: null,
            // sortKey: '', // Commented out - sorting functionality disabled
            // sortDirection: 'asc', // Commented out - sorting functionality disabled
            columnFilters: {},
            columnWidths: {},
            draggedColumn: null,
            attributeColumns: [],
            draggedAttributeColumn: null,
            isLoading: false,
            originalDataSource: null,
            quarters: [],
            cols: [],
            isVisibilityMenuOpened: false,
            measureVisibilityList: [],
            hasUnsavedEdits: false,
            isSuccessModalOpen: false,
            successModalMessage: '',
            isColorLegendModalOpen: false
        };
    }

    componentDidMount() {
        // Don't load data on mount - wait for Apply button click
        this.setState({ isLoading: false });
        if (this.props.onLoadEnd) {
            this.props.onLoadEnd();
        }
    }

    componentDidUpdate(prevProps: Props) {
        // Check if filter apply was triggered
        if (!prevProps.isCallRequired && this.props.isCallRequired) {
            this.setState({
                isLoading: true
            }, () => {
                this.getRunRatePlanGridData();
            });
        }
    }

    // Get run rate plan grid data from API
    getRunRatePlanGridData(): void {
        const baseParams = {
            userId: sessionStorage.getItem('userName'),
            planId: GetPlanId(this.props.history.location.pathname)
        };

        this.setState({ isLoading: true });

        // Get historic selection first
        GetHistoricSelection(baseParams )
            .then((historicRes: any) => {
                const savedHistoricWeeks = historicRes?.selectedHistoricValue || 6;
                const savedHistoricViewType = historicRes?.selectedHistoricView || 'RR only';

                // Add new param before calling GetRunRatePlanData
                const runRateParams = {
                    ...baseParams,
                    selectedHisticViewType: savedHistoricViewType
                };

                // Then get grid data
                return GetRunRatePlanData(runRateParams).then((res: any) => ({
                    gridData: res,
                    selectedHistoricWeeks: savedHistoricWeeks,
                    selectedHisticViewType: savedHistoricViewType
                }));
            })
            .then((result: any) => {
                const res = result.gridData;
                const selectedHistoricWeeks = result.selectedHistoricWeeks;
                const selectedHisticViewType = result.selectedHisticViewType;
                const oldHistoricViewTypeSelection = this.state.selectedHisticViewType;

                if (res) {
                    // Get quarters from API
                    const quarters = res.quarters || [];

                    // Initialize expanded state for all quarters
                    const expandedQuarters = {};
                    quarters.forEach(quarter => {
                        expandedQuarters[quarter.name] = quarter.isExpanded || false;
                    });

                    // Get cols from API
                    const cols = res.cols || [];

                    // Initialize column filters and widths
                    const columnFilters: { [key: string]: { value: string; condition: string } } = {};
                    const columnWidths: { [key: string]: number } = {};

                    cols.forEach(group => {
                        group.columns.forEach(col => {
                            columnFilters[col.key] = { value: '', condition: '=' };
                            columnWidths[col.key] = col.width || 60; // Default width (50% reduced)
                        });
                    });

                    // Get attribute columns and sort by sequence
                    const attributeGroup = cols.find(g => g.groupKey === 'attributes');
                    const attributeColumns = attributeGroup ? [...attributeGroup.columns].sort((a: any, b: any) => (a.sequence || 0) - (b.sequence || 0)) : [];

                    // Initialize filters for attribute columns too
                    attributeColumns.forEach(col => {
                        if (!columnFilters[col.key]) {
                            columnFilters[col.key] = { value: '', condition: '=' };
                            columnWidths[col.key] = col.width; // Default width (50% reduced)
                        }
                    });

                    // Initialize measure visibility list for attribute columns
                    const measureVisibilityList = attributeColumns.map(col => ({
                        key: col.key,
                        header: col.header,
                        isVisible: col.isVisible !== undefined ? col.isVisible : true,
                        prevCheck: col.isVisible !== undefined ? col.isVisible : true
                    }));

                    // Update rows from API response
                    const sortedRows = res.rows ? [...res.rows].sort((a, b) => a.sequence - b.sequence) : [];

                    // Initialize original week values for each row
                    sortedRows.forEach((row: any) => {
                        // Initialize edit tracking flags
                        row.isPlanRREdited = false;
                        row.isQuarterEdited = false;
                        row.isEdited = false;

                        if (row.quarters) {
                            row.quarters.forEach((quarter: any) => {
                                // Find the quarter definition from global quarters array
                                const quarterDef = quarters.find(q => q.name === quarter.name);

                                // Add isPast and key metadata from quarter definition
                                if (quarterDef) {
                                    quarter.isPast = quarterDef.isPast !== undefined ? quarterDef.isPast : false;
                                    quarter.key = quarterDef.key || '';
                                }

                                if (quarter.weeks) {
                                    quarter.weeks.forEach((week: any) => {
                                        if (quarterDef && !quarterDef.isPast) {
                                            week.oldValue = week.value;
                                        }
                                    });
                                }
                            });
                            row.originalQuarters = JSON.parse(JSON.stringify(row.quarters));
                        }
                    });

                    // FOR TESTING: Copy 1st row's SKU to 2nd row's prod_attribute2 to enable NPI icon
                   /* if (sortedRows.length >= 2) {
                        if (!sortedRows[1].attributes) {
                            sortedRows[1].attributes = {};
                        }
                        sortedRows[1].attributes.prod_attribute2 = sortedRows[0].sku;
                        console.log('Testing: Set 2nd row prod_attribute2 to:', sortedRows[0].sku);
                    }*/

                    this.setState({
                        originalDataSource: res,
                        originalRows: JSON.parse(JSON.stringify(sortedRows)),
                        quarters,
                        cols,
                        expandedQuarters,
                        columnFilters,
                        columnWidths,
                        attributeColumns,
                        measureVisibilityList,
                        selectedHistoricWeeks,
                        selectedHisticViewType,
                        oldHistoricViewTypeSelection,
                        isLoading: false,
                        isAutoLoad: false,
                    }, () => {
                        // Apply filters on initial load
                        this.applyFiltersAndPagination();
                        if (this.props.onLoadEnd) {
                            this.props.onLoadEnd();
                        }
                        if (this.props.onApiCallComplete) {
                            this.props.onApiCallComplete();
                        }
                    });
                } else {
                    this.setState({ isLoading: false });
                    if (this.props.onLoadEnd) {
                        this.props.onLoadEnd();
                    }
                    if (this.props.onApiCallComplete) {
                        this.props.onApiCallComplete();
                    }
                }
            })
            .catch(() => {
                this.setState({ isLoading: false });
                if (this.props.onLoadEnd) {
                    this.props.onLoadEnd();
                }
                if (this.props.onApiCallComplete) {
                    this.props.onApiCallComplete();
                }
            });
    }

    // Toggle quarter expand/collapse
    toggleQuarter = (quarterName: string) => {
        this.setState({
            expandedQuarters: {
                ...this.state.expandedQuarters,
                [quarterName]: !this.state.expandedQuarters[quarterName]
            }
        });
    };

    // Toggle attributes expand/collapse
    toggleAttributes = () => {
        this.setState({
            isAttributesExpanded: !this.state.isAttributesExpanded
        });
    };

    // Toggle More menu
    toggleMoreMenu = () => {
        this.setState({
            isMoreMenuOpened: !this.state.isMoreMenuOpened
        });
    };

    // Toggle Export submenu
    toggleExportMenu = () => {
        this.setState({
            isExportMenuOpened: !this.state.isExportMenuOpened
        });
    };

    // Toggle Import submenu
    toggleImportMenu = () => {
        this.setState({
            isImportMenuOpened: !this.state.isImportMenuOpened
        });
    };

    // Toggle Visibility menu
    toggleVisibilityMenu = () => {
        this.setState({
            isVisibilityMenuOpened: !this.state.isVisibilityMenuOpened
        });
    };

    // Update visibility check for a measure
    onUpdateVisibilityCheck = (measureIdx: number) => {
        const measureVisibilityList = [...this.state.measureVisibilityList];
        measureVisibilityList[measureIdx].isVisible = !measureVisibilityList[measureIdx].isVisible;

        // Update the attributeColumns array to reflect visibility
        const attributeColumns = [...this.state.attributeColumns];
        const columnIndex = attributeColumns.findIndex(col => col.key === measureVisibilityList[measureIdx].key);
        if (columnIndex !== -1) {
            attributeColumns[columnIndex].isVisible = measureVisibilityList[measureIdx].isVisible;
        }

        this.setState({
            measureVisibilityList,
            attributeColumns
        });
    };

    // Check if visibility save button should be disabled
    checkVisibilitySaveButton = (): boolean => {
        return this.state.measureVisibilityList.every(
            measure => measure.isVisible === measure.prevCheck
        );
    };

    // Update measure visibility - save to backend
    updateMeasureVisibility = () => {
        const payload: IMeasureVisibilityPayload = {
            visibility: this.state.measureVisibilityList.map(measure => ({
                key: measure.key,
                isVisible: measure.isVisible
            }))
        };

        const params = {
            userId: sessionStorage.getItem('userName'),
            planId: GetPlanId(this.props.history.location.pathname)
        };

        this.setState({ isLoading: true });

        UpdateRunRatePlanMeasureVisibility(payload, params)
            .then((res) => {
                if (res) {
                    // Update prevCheck to match current state
                    const measureVisibilityList = this.state.measureVisibilityList.map(measure => ({
                        ...measure,
                        prevCheck: measure.isVisible
                    }));
                    const message = 'Measure Visibility Saved Successfully.';
                    this.setState({
                        measureVisibilityList,
                        isVisibilityMenuOpened: false,
                        isLoading: false,
                        isSuccessModalOpen: true,
                        successModalMessage: message
                    });
                } else {
                    this.setState({ isLoading: false });
                }
            })
            .catch(error => {
                console.error('Error saving visibility settings:', error);
                this.setState({ isLoading: false });
            });
    };

    // Save column sequence
    saveMeasureSequence = () => {
        const payload: IMeasureSequencePayload[] = this.state.attributeColumns.map((col) => ({
            key: col.key,
            sequence: col.sequence,
            width: this.state.columnWidths[col.key] || 60
        }));

        const params = {
            userId: sessionStorage.getItem('userName'),
            planId: GetPlanId(this.props.history.location.pathname)
        };

        this.setState({ isLoading: true });

        SaveRunRatePlanMeasureSequenceAndWidth(payload, params)
            .then((res) => {
                if (res) {
                    const message = 'Measure Sequence And Width Saved Successfully.';
                    this.setState({
                        isLoading: false,
                        isSuccessModalOpen: true,
                        successModalMessage: message
                    });
                } else {
                    this.setState({ isLoading: false });
                }
            })
            .catch(error => {
                console.error('Error saving column sequence:', error);
                this.setState({ isLoading: false });
            });
    };

    // Save SKU row sequence
    saveSkuSequence = () => {
        const payload: ISkuSequencePayload[] = this.state.rows.map((row) => ({
            key: row.sku,
            sequence: row.sequence
        }));

        const params = {
            userId: sessionStorage.getItem('userName'),
            planId: GetPlanId(this.props.history.location.pathname)
        };

        this.setState({ isLoading: true });

        SaveRunRatePlanSkuSequence(payload, params)
            .then((res) => {
                if (res) {
                    const message = 'SKU sequence saved successfully.';
                    // Update original rows to match current order
                    this.setState({
                        originalRows: JSON.parse(JSON.stringify(this.state.rows)),
                        hasSequenceChanged: false,
                        isLoading: false,
                        isSuccessModalOpen: true,
                        successModalMessage: message
                    });
                } else {
                    this.setState({ isLoading: false });
                }
            })
            .catch(error => {
                console.error('Error saving SKU sequence:', error);
                this.setState({ isLoading: false });
            });
    };

    // Static utility for numeric comparisons
    static aggregateFn(symbol: string, a: number, b: number): boolean {
        switch (symbol) {
            case '=':
                return a === b;
            case '<':
                return a < b;
            case '>':
                return a > b;
            case '<=':
                return a <= b;
            case '>=':
                return a >= b;
            default:
                return true;
        }
    }

    // Update filter condition (operator) for a column
    updateFilterCondition = (columnKey: string, condition: string) => {
        this.setState(prevState => ({
            columnFilters: {
                ...prevState.columnFilters,
                [columnKey]: {
                    value: prevState.columnFilters[columnKey]?.value || '',
                    condition
                }
            }
        }), this.applyFiltersAndPagination);
    };

    // Update filter value for a column
    updateFilterValue = (columnKey: string, value: string) => {
        this.setState(prevState => ({
            columnFilters: {
                ...prevState.columnFilters,
                [columnKey]: {
                    condition: prevState.columnFilters[columnKey]?.condition || '=',
                    value
                }
            }
        }), this.applyFiltersAndPagination);
    };

    // Apply filters, sorting, and pagination
    applyFiltersAndPagination = () => {
        let filteredRows = [...this.state.originalRows];

        // Apply column filters
        Object.keys(this.state.columnFilters).forEach(columnKey => {
            const filter = this.state.columnFilters[columnKey];
            if (filter.value) {
                filteredRows = filteredRows.filter(row => {
                    let cellValue: any;

                    // Check if this is a week column filter (pattern: QuarterName_WeekName)
                    // Week columns contain underscore and the second part looks like a week name (e.g., "2025-WK25")
                    // Examples: "13-25 weeks_2025-WK25", "2025-Q4_2025-WK33", "0-12 weeks_2025-WK11"
                    // Must contain "WK" to be a week column (week names like "2025-WK25" always have "WK")
                    const isWeekColumn = columnKey.includes('_') && columnKey.includes('WK');

                    if (isWeekColumn) {
                        const parts = columnKey.split('_');
                        if (parts.length >= 2) {
                            // Extract quarter name and week name
                            const quarterName = parts[0];
                            const weekName = parts.slice(1).join('_'); // In case week name has underscores

                            // Find the quarter in the row's quarters array
                            const quarter = row.quarters?.find((q: any) => q.name === quarterName);
                            if (quarter) {
                                // Find the week value
                                const weekData = quarter.weeks?.find((w: any) => w.name === weekName);
                                if (weekData !== undefined) {
                                    cellValue = weekData.value;
                                } else {
                                    // If not found by name, try by index using weekNames
                                    const weekIndex = quarter.weekNames?.indexOf(weekName);
                                    if (weekIndex !== -1 && quarter.weeks && quarter.weeks[weekIndex] !== undefined) {
                                        cellValue = quarter.weeks[weekIndex].value;
                                    }
                                }
                            }

                            // If we found a week value, apply numeric comparison
                            if (cellValue !== undefined && cellValue !== null) {
                                const numValue = typeof cellValue === 'number' ? cellValue : parseFloat(cellValue);
                                const filterNum = parseFloat(filter.value);
                                const condition = filter.condition || '=';
                                if (isNaN(numValue) || isNaN(filterNum)) return false;
                                return RunRatePlanTable.aggregateFn(condition, numValue, filterNum);
                            }
                            return false;
                        }
                    }

                    // Get value from different locations (original logic)
                    if (row[columnKey] !== undefined) {
                        cellValue = row[columnKey];
                    } else if (row.attributes && row.attributes[columnKey] !== undefined) {
                        cellValue = row.attributes[columnKey];
                    } else if (row.actions && row.actions[columnKey] !== undefined) {
                        cellValue = row.actions[columnKey];
                    }

                    // Special handling for calculated columns (historicalRR, planRR, wos_po)
                    if (columnKey === 'historicalRR') {
                        cellValue = this.calculateHistoricRR(row);
                    } else if (columnKey === 'planRR') {
                        cellValue = this.calculatePlanRR(row);
                    } else if (columnKey === 'wos_po') {
                        cellValue = this.calculateWosPo(row);
                    } else if (columnKey === 'wos') {
                        cellValue = this.calculateWos(row);
                    }

                    // For number columns, treat null/undefined as 0 for comparison
                    // First check if we need to find column def to know dataType
                    let tempColumnDef: any = null;
                    this.state.cols.forEach(group => {
                        const col = group.columns.find(c => c.key === columnKey);
                        if (col) tempColumnDef = col;
                    });
                    if (!tempColumnDef) {
                        tempColumnDef = this.state.attributeColumns.find(c => c.key === columnKey);
                    }

                    // For number-type columns, allow null/undefined to be treated as 0
                    const isNumberColumn = tempColumnDef && (tempColumnDef.dataType === 'number' || tempColumnDef.dataType === 'currency' || tempColumnDef.dataType === 'percentage');

                    if (cellValue === undefined || cellValue === null) {
                        if (isNumberColumn) {
                            cellValue = 0; // Treat null/undefined as 0 for number columns
                        } else {
                            return false;
                        }
                    }

                    // Find column definition to get dataType
                    let columnDef: any = null;
                    this.state.cols.forEach(group => {
                        const col = group.columns.find(c => c.key === columnKey);
                        if (col) columnDef = col;
                    });

                    // Also check attribute columns
                    if (!columnDef) {
                        columnDef = this.state.attributeColumns.find(c => c.key === columnKey);
                    }

                    if (!columnDef) {
                        return false;
                    }

                    // String search (case-insensitive)
                    if (columnDef.dataType.toLowerCase() === 'string') {
                        if (cellValue === undefined || cellValue === null) {
                            return false;
                        }
                        const cellString = cellValue.toString().toUpperCase();
                        const filterString = filter.value.toString().toUpperCase();
                        return cellString.includes(filterString);
                    }

                    // Number comparison (including currency and percentage)
                    if (columnDef.dataType === 'number' || columnDef.dataType === 'currency' || columnDef.dataType === 'percentage') {
                        const numValue = typeof cellValue === 'number' ? cellValue : parseFloat(cellValue);
                        const filterNum = parseFloat(filter.value);
                        const condition = filter.condition || '=';
                        if (isNaN(numValue) || isNaN(filterNum)) {
                            return false;
                        }
                        const result = RunRatePlanTable.aggregateFn(condition, numValue, filterNum);
                        return result;
                    }

                    return true;
                });
            }
        });

        // Apply sorting - COMMENTED OUT - sorting functionality disabled
        /* if (this.state.sortKey) {
            filteredRows.sort((a, b) => {
                let aValue: any, bValue: any;

                // Get values for sorting
                if (a[this.state.sortKey] !== undefined) {
                    aValue = a[this.state.sortKey];
                    bValue = b[this.state.sortKey];
                } else if (a.attributes && a.attributes[this.state.sortKey] !== undefined) {
                    aValue = a.attributes[this.state.sortKey];
                    bValue = b.attributes[this.state.sortKey];
                } else if (a.actions && a.actions[this.state.sortKey] !== undefined) {
                    aValue = a.actions[this.state.sortKey];
                    bValue = b.actions[this.state.sortKey];
                }

                if (aValue === undefined || bValue === undefined) return 0;

                // Compare values
                let comparison = 0;
                if (typeof aValue === 'string') {
                    comparison = aValue.localeCompare(bValue);
                } else {
                    comparison = aValue - bValue;
                }

                return this.state.sortDirection === 'asc' ? comparison : -comparison;
            });
        } */

        // Display all filtered rows without pagination
        this.setState({
            rows: filteredRows
        });
    };

    // Handle column sort - COMMENTED OUT - sorting functionality disabled
    /* handleSort = (columnKey: string) => {
        this.setState(prevState => {
            let newSortKey = columnKey;
            let newSortDirection = 'asc';

            if (prevState.sortKey === columnKey) {
                if (prevState.sortDirection === 'asc') {
                    newSortDirection = 'desc';
                } else if (prevState.sortDirection === 'desc') {
                    // Reset to original (no sorting)
                    newSortKey = '';
                    newSortDirection = '';
                }
            }

            return {
                sortKey: newSortKey,
                sortDirection: newSortDirection
            };
        }, this.applyFiltersAndPagination);
    }; */

    // Handle column resize start
    handleColumnResizeStart = (columnKey: string, e: React.MouseEvent) => {
        e.preventDefault();
        this.setState({
            draggedColumn: {
                key: columnKey,
                iniMouse: e.clientX,
                iniSize: this.state.columnWidths[columnKey] || 60
            }
        });

        document.addEventListener('mousemove', this.handleColumnResize);
        document.addEventListener('mouseup', this.handleColumnResizeEnd);
    };

    // Handle column resize
    handleColumnResize = (e: MouseEvent) => {
        if (!this.state.draggedColumn) return;

        const { key, iniMouse, iniSize } = this.state.draggedColumn;
        const delta = e.clientX - iniMouse;
        const newWidth = Math.max(36, iniSize + delta); // Minimum width 36px (30% of 120px)

        this.setState(prevState => ({
            columnWidths: {
                ...prevState.columnWidths,
                [key]: newWidth
            }
        }));
    };

    // Handle column resize end
    handleColumnResizeEnd = () => {
        this.setState({ draggedColumn: null });
        document.removeEventListener('mousemove', this.handleColumnResize);
        document.removeEventListener('mouseup', this.handleColumnResizeEnd);
    };

    // Handle attribute column drag start
    handleAttributeColumnDragStart = (e: React.DragEvent, colIndex: number) => {
        this.setState({ draggedAttributeColumn: colIndex });
        e.dataTransfer.effectAllowed = 'move';
    };

    // Handle attribute column drag over
    handleAttributeColumnDragOver = (e: React.DragEvent, colIndex: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    // Handle attribute column drop
    handleAttributeColumnDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        const dragIndex = this.state.draggedAttributeColumn;

        if (dragIndex === null || dragIndex === dropIndex) return;

        // Get visible columns only (same as what's rendered)
        const visibleColumns = this.state.attributeColumns.filter(col => col.isVisible !== false);

        // Map filtered indices to full array indices
        const fullDragIndex = this.state.attributeColumns.indexOf(visibleColumns[dragIndex]);
        const fullDropIndex = this.state.attributeColumns.indexOf(visibleColumns[dropIndex]);

        // Reorder in full array using mapped indices
        const newColumns = [...this.state.attributeColumns];
        const [draggedColumn] = newColumns.splice(fullDragIndex, 1);
        newColumns.splice(fullDropIndex, 0, draggedColumn);

        // Update sequence numbers for ALL columns
        newColumns.forEach((col, index) => {
            col.sequence = index + 1;
        });

        this.setState({
            attributeColumns: newColumns,
            draggedAttributeColumn: null
        });
    };

    // Handle attribute column drag end
    handleAttributeColumnDragEnd = () => {
        this.setState({ draggedAttributeColumn: null });
    };

    // Check if any column is expanded
    isColumnExpanded(): boolean {
        return Object.values(this.state.expandedQuarters).some(val => val);
    }

    // Get value from row based on groupKey and columnKey
    getRowValue(row: any, groupKey: string, columnKey: string): any {
        if (groupKey === 'fixed') {
            return row[columnKey];
        }
        return row[groupKey]?.[columnKey];
    }

    // Get week value from row quarters
    getWeekValue(row: any, quarterName: string, weekName: string): any {
        const quarter = row.quarters?.find(q => q.name === quarterName);
        const week = quarter?.weeks?.find(w => w.name === weekName);
        return week?.value;
    }

    // Get week isPast flag
    getWeekIsPast(row: any, quarterName: string, weekName: string): boolean {
        const quarter = row.quarters?.find(q => q.name === quarterName);
        const week = quarter?.weeks?.find(w => w.name === weekName);
        return week?.isPast || false;
    }

    // Render cell based on column configuration
    renderCell(column: any, value: any, groupKey: string, row: any, rowIdx: number): React.ReactNode {
        // Handle icon columns
        if (column.dataType === 'icon') {
            if (column.key === 'npi' && value === true) {
                // Check if predecessorSku is empty - if empty, don't show NPI icon
                const predSku = row.attributes?.prod_attribute2;
                if (!predSku || predSku === '') {
                    return null;
                }
                return <i className="ri-add-line text-success font-size-18 cursor-pointer"
                          onClick={() => this.handleNPIClick(rowIdx)} style={{ cursor: 'pointer' }} />;
            }
            if (column.key === 'eol' && value === true) {
                // Check if both stock_onhand_qty and incoming_inv_qty are 0 - if so, don't show EOL icon
                const stock = row.attributes?.stock_onhand_qty || 0;
                const incoming = row.attributes?.incoming_inv_qty || 0;
                if (stock === 0 && incoming === 0) {
                    return null;
                }
                return <i className="ri-subtract-line text-danger font-size-18 cursor-pointer"
                          onClick={() => this.handleEOLClick(rowIdx)} style={{ cursor: 'pointer' }} />;
            }
            return null;
        }

        // Handle empty values
        if (value === null || value === undefined || value === '') {
            return '-';
        }

        // Special handling for editable cells
        if (this.state.isEditable) {
            // Historic RR - draggable but not editable
            if (groupKey === 'actions' && column.key === 'historicalRR') {
                const calculatedValue = this.calculateHistoricRR(row);
                return (
                    <span
                        draggable
                        onDragStart={(e) => this.handleHistoricRRDragStart(e, rowIdx, calculatedValue)}
                        style={{ cursor: 'grab' }}
                        className="text-primary font-weight-bold"
                    >
                        {calculatedValue.toLocaleString()}
                    </span>
                );
            }

            // Plan RR - editable and droppable
            if (groupKey === 'actions' && column.key === 'planRR') {
                const calculatedValue = this.calculatePlanRR(row);
                const isDisabled = row.isQuarterEdited;
                return (
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="editable-input text-right"
                        value={calculatedValue}
                        disabled={isDisabled}
                        onDrop={(e) => !isDisabled && this.handlePlanRRDrop(e, rowIdx)}
                        onDragOver={(e) => !isDisabled && e.preventDefault()}
                        onChange={(e) => !isDisabled && this.handlePlanRREdit(rowIdx, parseFloat(e.target.value) || 0)}
                        style={{ width: '40px', backgroundColor: isDisabled ? '#f5f5f5' : '#fff' }}
                    />
                );
            }
        }

        // Calculate and display Historic RR and Plan RR in non-editable mode
        if (groupKey === 'actions' && column.key === 'historicalRR') {
            const calculatedValue = this.calculateHistoricRR(row);
            return (
                <span
                    draggable
                    onDragStart={(e) => this.handleHistoricRRDragStart(e, rowIdx, calculatedValue)}
                    style={{ cursor: 'grab' }}
                    className="text-primary font-weight-bold"
                >
                  {calculatedValue.toLocaleString()}
                </span>
            );
        }
        if (groupKey === 'actions' && column.key === 'planRR') {
            const calculatedValue = this.calculatePlanRR(row);
            const isDisabled = row.isQuarterEdited;
            return (
                <span
                    onDrop={(e) => !isDisabled && this.handlePlanRRDrop(e, rowIdx)}
                    onDragOver={(e) => !isDisabled && e.preventDefault()}
                    style={{
                        cursor: isDisabled ? 'not-allowed' : 'default',
                        display: 'inline-block',
                        padding: '2px 4px',
                        backgroundColor: isDisabled ? '#f5f5f5' : 'transparent'
                    }}
                >
                  {calculatedValue.toLocaleString()}
                </span>
            );
        }

        // Calculate and display WOS PO (wos_po)
        if (groupKey === 'attributes' && column.key === 'wos_po') {
            const calculatedValue = this.calculateWosPo(row);
            return calculatedValue.toLocaleString();
        }

        // Calculate and display WOS (wos)
        if (groupKey === 'attributes' && column.key === 'wos') {
            const calculatedValue = this.calculateWos(row);
            return calculatedValue.toLocaleString();
        }

        // Handle number columns
        if (column.dataType === 'number' || column.dataType === 'currency' || column.dataType === 'percentage') {
            const numValue = typeof value === 'number' ? value : parseFloat(value);
            const isNegative = numValue < 0;
            const textClass = column.showNegativeInRed && isNegative ? 'text-danger' : '';

            let displayValue = numValue.toLocaleString();

            // Add currency symbol
            if (column.dataType === 'currency') {
                displayValue = `$${displayValue}`;
            }

            // Add percentage symbol
            if (column.dataType === 'percentage') {
                displayValue = `${displayValue}%`;
            }

            return <span className={textClass}>{displayValue}</span>;
        }

        // Handle string columns
        return value;
    }

    // Render week cell for quarters
    renderWeekCell(row: any, rowIdx: number, quarterName: string, weekName: string): React.ReactNode {
        const quarter = row.quarters?.find(q => q.name === quarterName);
        const week = quarter?.weeks?.find(w => w.name === weekName);

        if (!week) return '-';

        const value = week.value;
        const isPast = week.isPast || false;

        // Find the quarter definition to check if it's a past quarter
        const quarterDef = this.state.quarters.find(q => q.name === quarterName);
        const isHistorical = quarterDef?.key === 'pastQuarter';

        // For historical weeks, just display the value (not editable)
        if (isHistorical) {
            const isNegative = value < 0;
            const textClass = isNegative ? 'text-danger' : '';
            return <span className={textClass}>{value.toLocaleString()}</span>;
        }

        // For future quarters, check if editable
        if (this.state.isEditable) {
            const isDisabled = row.isPlanRREdited || isPast;
            return (
                <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="editable-input text-right"
                    value={value}
                    disabled={isDisabled}
                    onChange={(e) => !isDisabled && this.handleQuarterWeekEdit(rowIdx, quarterName, weekName, parseFloat(e.target.value) || 0)}
                    style={{ width: '40px', backgroundColor: isDisabled ? '#f5f5f5' : '#fff' }}
                />
            );
        }

        // Default display
        return <span>{value.toLocaleString()}</span>;
    }

    // Get sticky class for fixed columns based on groupKey
    getStickyClass(column: any, groupKey: string, isHeader: boolean): string {
        // Only fixed columns are sticky
        if (groupKey !== 'fixed') return '';

        const fixedGroup = this.state.cols.find(col => col.groupKey === 'fixed');
        const stickyIndex = fixedGroup?.columns.findIndex(col => col.key === column.key);
        if (stickyIndex === -1 || stickyIndex === undefined) return '';

        const stickyClass = styles[`sticky-col-${stickyIndex + 1}`];
        return stickyClass;
    }

    // Get alignment based on dataType
    getAlignment(column: any): string {
        // Icon columns are always center-aligned
        if (column.dataType === 'icon') return 'center';
        // Number columns are right-aligned
        if (['number', 'currency', 'percentage'].includes(column.dataType)) return 'right';
        // String columns are left-aligned
        if (column.dataType === 'string') return 'left';
        // Default to center for headers
        return 'center';
    }

    // Get cell styles for weeks based on editable mode and past/historical status
    getCellStyles(quarterName: string, weekName: string, row: any): React.CSSProperties {
        const cellStyle: React.CSSProperties = {
            maxWidth: '60px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        };

        const quarter = row.quarters?.find(q => q.name === quarterName);
        const week = quarter?.weeks?.find(w => w.name === weekName);

        if (!week) return cellStyle;

        // Find the quarter definition to check if it's a past quarter
        const quarterDef = this.state.quarters.find(q => q.name === quarterName);
        const isHistorical = quarterDef?.key === 'pastQuarter';
        const isPast = week.isPast;

        // Apply conditional formatting for historical quarters based on availability and discount
        if (isHistorical) {
            const availRatio = week.availability;
            const discount = week.discount;

            // Determine background color based on availability ratio
            if (availRatio !== null && availRatio !== undefined) {
                if (availRatio < 50) {
                    cellStyle.backgroundColor = '#ffcccc'; // Light Red
                } else if (availRatio < 80) {
                    cellStyle.backgroundColor = '#ffffcc'; // Light Yellow
                } else {
                    cellStyle.backgroundColor = '#ccffcc'; // Light Green
                }
            }

            // Determine border color based on discount %
            if (discount !== null && discount !== undefined) {
                if (discount > 15) {
                    cellStyle.border = '1px solid #cc0000'; // Dark Red
                } else if (discount > 5) {
                    cellStyle.border = '1px solid #cccc00'; // Dark Yellow
                } else {
                    cellStyle.border = '1px solid #00cc00'; // Dark Green
                }
            }

            return cellStyle;
        }

        // In uneditable mode, add light gray background only for past weeks in current quarters (not historical quarters)
        if (!this.state.isEditable && isPast) {
            cellStyle.backgroundColor = '#d4d4d4'; // Light gray background for past weeks in current quarters
            return cellStyle;
        }

        return cellStyle;
    }

    // Handle drag start
    handleDragStart = (e: React.DragEvent, index: number) => {
        this.setState({ draggedRowIndex: index });
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', '');
    };

    // Handle drag over
    handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        const { draggedRowIndex, rows } = this.state;
        if (draggedRowIndex === null || draggedRowIndex === index) return;

        // Create a new array with the shifted rows
        const newRows = [...rows];
        const draggedRow = newRows[draggedRowIndex];

        // Remove the dragged row from its current position
        newRows.splice(draggedRowIndex, 1);

        // Insert it at the new position
        newRows.splice(index, 0, draggedRow);

        // Update sequences
        newRows.forEach((row, idx) => {
            row.sequence = idx + 1;
        });

        // Check if sequence has changed
        const hasChanged = this.checkSequenceChanged(newRows);

        this.setState({
            rows: newRows,
            draggedRowIndex: index,
            hasSequenceChanged: hasChanged
        });
    };

    // Handle drag end
    handleDragEnd = () => {
        this.setState({ draggedRowIndex: null });
    };

    // Check if sequence has changed
    checkSequenceChanged = (currentRows: any[]): boolean => {
        const { originalRows } = this.state;

        if (currentRows.length !== originalRows.length) return true;

        for (let i = 0; i < currentRows.length; i++) {
            if (currentRows[i].sku !== originalRows[i].sku ||
                currentRows[i].sequence !== originalRows[i].sequence) {
                return true;
            }
        }

        return false;
    };

    // Format week name from "2025-WK35" to "WK35"
    formatWeekName = (weekName: string): string => {
        if (typeof weekName === 'string' && weekName.includes('-WK')) {
            const parts = weekName.split('-WK');
            return `WK${parts[1]}`;
        }
        return weekName;
    };

    // Calculate Historic RR based on selected weeks
    calculateHistoricRR = (row: any): number => {
        const selectedWeeks = this.state.selectedHistoricWeeks;

        // Get all past quarters
        const pastQuarters = this.state.quarters.filter(q => q.isPast);

        // Get current quarter (first non-past quarter)
        const currentQuarter = this.state.quarters.find(q => !q.isPast);

        // Collect all weeks from all past quarters (only weeks marked as past)
        const allPastWeeks: any[] = [];
        const allCurrentWeeks: any[] = [];

        // Check all quarters (past and present) and separate weeks by isPast flag
        row.quarters?.forEach((quarter, idx) => {
            if (quarter.weeks) {
                quarter.weeks.forEach(week => {
                    if (week.isPast && !(quarter.key === 'currentQuarter' || quarter.key === 'futureQuarter')) {
                        allPastWeeks.push(week);
                    } else {
                        allCurrentWeeks.push(week);
                    }
                });
            }
        });

        // Check if last week of past and first week of current are the same
        let skipLastWeek = false;
        if (allPastWeeks.length > 0 && allCurrentWeeks.length > 0) {
            const lastPastWeek = allPastWeeks[allPastWeeks.length - 1];
            const firstCurrentWeek = allCurrentWeeks[0];

            // Format and compare week names (handle formats like "2025-WK49" or "wk49")
            const lastWeekName = this.formatWeekName(lastPastWeek.name || '').toUpperCase();
            const firstWeekName = this.formatWeekName(firstCurrentWeek.name || '').toUpperCase();

            if (lastWeekName && firstWeekName && lastWeekName === firstWeekName) {
                skipLastWeek = true;
            }
        }

        // Get the last N weeks based on selectedHistoricWeeks
        // If skipLastWeek is true, skip the last week and take the previous N weeks
        let weeksToConsider;
        if (skipLastWeek) {
            // Skip the last week and take the previous N weeks
            // For example, if selectedWeeks = 3 and we skip wk49,
            // we take weeks at positions -4, -3, -2 (e.g., wk48, 47, 46)
            // Remove the last week first, then take the last N weeks
            const weeksWithoutLast = allPastWeeks.slice(0, -1);
            weeksToConsider = weeksWithoutLast.slice(-selectedWeeks);
        } else {
            weeksToConsider = allPastWeeks.slice(-selectedWeeks);
        }

        let sum = 0;
        let count = 0;
    console.log('weeksToConsider=', weeksToConsider);
        weeksToConsider.forEach(week => {
            const value = week.value;
            if (value !== null && value !== undefined) {
                sum += Math.abs(value); // Use absolute value for historical data
                count++;
            }
        });
        console.log('sum=', sum, ', count=', count, ', output=', Math.round(sum / count));
        const result = count > 0 ? Math.round(sum / count) : 0;

        return result;
    };

    // Calculate Plan RR as average of current + future quarters (non-past weeks)
    calculatePlanRR = (row: any): number => {
        let sum = 0;
        let count = 0;

        // Find non-historical quarters (those with isPast = false in global definition)
        const futureQuarters = this.state.quarters.filter(q => !q.isPast);

        futureQuarters.forEach(quarterDef => {
            const rowQuarter = row.quarters?.find(q => q.name === quarterDef.name);
            if (!rowQuarter) return;

            rowQuarter.weeks.forEach(week => {
                // Only include non-past weeks
                if (!week.isPast) {
                    const value = week.value;
                    if (value !== null && value !== undefined) {
                        sum += value;
                        count++;
                    }
                }
            });
        });

        return count > 0 ? Math.round(sum / count) : 0;
    };

    // Calculate WOS PO as - ((stock_onhand_qty + incoming_inv_qty) / planRR)
    calculateWosPo = (row: any): number => {
        const stockOnhand = row.attributes?.stock_onhand_qty || 0;
        const incomingInv = row.attributes?.incoming_inv_qty || 0;
        const planRR = this.calculatePlanRR(row);

        // Avoid division by zero
        if (planRR === 0) return 0;

        return parseFloat(((stockOnhand + incomingInv) / planRR).toFixed(1));
    };

    // Calculate WOS as - (stock_onhand_qty / planRR)
    calculateWos = (row: any): number => {
        const stockOnhand = row.attributes?.stock_onhand_qty || 0;
        const planRR = this.calculatePlanRR(row);

        // Avoid division by zero
        if (planRR === 0) return 0;

        return parseFloat((stockOnhand / planRR).toFixed(1));
    };

    // Handle historic weeks selection change
    handleHistoricChange = (value: number, viewType: string) => {
        const isNewViewTypeSelected = viewType !== this.state.oldHistoricViewTypeSelection;
        this.setState({ isLoading: true, selectedHistoricWeeks: value, selectedHisticViewType: viewType }, () => {
            // Save the selection to backend
            const params = {
                userId: sessionStorage.getItem('userName'),
                planId: GetPlanId(this.props.history.location.pathname),
                selectedHistoricWeek: value,
                selectedHistoricViewType: viewType
            };

            SaveHistoricSelection(params)
                .then(() => {
                    if(isNewViewTypeSelected){
                        //Reload the grid with selected view type data
                        this.getRunRatePlanGridData();
                    }else{
                        this.setState({ isLoading: false });
                    }
                    
                    // Re-render will happen automatically due to setState
                })
                .catch((error) => {
                    this.setState({ isLoading: false });
                    console.error('Failed to save historic selection:', error);
                });
        });
    };

    // Get number of weeks left in future quarters (non-past weeks)
    getWeeksInFutureQuarters = (): number => {
        let count = 0;
        const futureQuarters = this.state.quarters.filter(q => !q.isPast);

        // We need to check the actual row data for isPast flags
        if (this.state.rows.length > 0) {
            const sampleRow = this.state.rows[0];
            futureQuarters.forEach(quarterDef => {
                const rowQuarter = sampleRow.quarters?.find(q => q.name === quarterDef.name);
                if (!rowQuarter) return;

                rowQuarter.weeks.forEach(week => {
                    if (!week.isPast) {
                        count++;
                    }
                });
            });
        }

        return count;
    };

    // Handle Plan RR edit and distribute to weeks
    handlePlanRREdit = (rowIndex: number, newPlanRR: number) => {
        // Validate: Don't allow negative values
        if (newPlanRR < 0) {
            return;
        }

        const updatedRows = this.state.rows.map((r, idx) => {
            if (idx === rowIndex) {
                // Deep copy the row we're editing
                return {
                    ...r,
                    quarters: JSON.parse(JSON.stringify(r.quarters)),
                    actions: { ...r.actions }
                };
            }
            return r;
        });
        const row = updatedRows[rowIndex];

        const futureQuarters = this.state.quarters.filter(q => !q.isPast);

        // Distribute quarter-wise (each quarter independently)
        futureQuarters.forEach(quarterDef => {
            const rowQuarter = row.quarters?.find(q => q.name === quarterDef.name);
            const originalQuarter = row.originalQuarters?.find(q => q.name === quarterDef.name);
            if (!rowQuarter || !originalQuarter) return;

            // Count future weeks in this quarter
            let futureWeeksInQuarter = 0;
            rowQuarter.weeks.forEach(week => {
                if (!week.isPast) {
                    futureWeeksInQuarter++;
                }
            });

            if (futureWeeksInQuarter === 0) return;

            // Calculate quarter total = newPlanRR × futureWeeksInQuarter
            const quarterTotal = newPlanRR * futureWeeksInQuarter;

            // Calculate old quarter total from ORIGINAL values in THIS quarter only
            let oldQuarterTotal = 0;
            originalQuarter.weeks.forEach(week => {
                if (!week.isPast) {
                    oldQuarterTotal += week.value || 0;
                }
            });

            // Collect future weeks in this quarter for rounding adjustment
            const futureWeeksInThisQuarter: { week: any; weekIdx: number }[] = [];
            rowQuarter.weeks.forEach((week, weekIdx) => {
                if (!week.isPast) {
                    futureWeeksInThisQuarter.push({ week, weekIdx });
                }
            });

            // Distribute within this quarter
            let distributedInQuarter = 0;

            futureWeeksInThisQuarter.forEach(({ week, weekIdx }) => {
                const originalValue = originalQuarter.weeks[weekIdx]?.value || 0;

                // If all weeks in quarter are 0: equal distribution
                // Otherwise: proportional distribution (zeros naturally get 0)
                const percentage = oldQuarterTotal !== 0
                    ? (originalValue / oldQuarterTotal)
                    : (1 / futureWeeksInQuarter);

                const newValue = percentage * quarterTotal;
                week.value = Math.round(newValue);
                distributedInQuarter += newValue;
            });

            // Adjust last week in quarter for rounding errors
            /* const difference = quarterTotal - distributedInQuarter;
            if (difference !== 0 && futureWeeksInThisQuarter.length > 0) {
                const lastWeek = futureWeeksInThisQuarter[futureWeeksInThisQuarter.length - 1].week;
                lastWeek.value.
                lastWeek.value += difference;
            } */
        });

        // Update Plan RR
        row.actions.planRR = newPlanRR;
        row.isPlanRREdited = true;
        row.isEdited = true;

        this.setState({
            rows: updatedRows,
            hasUnsavedEdits: true
        });
    };

    // Handle quarter week edit
    handleQuarterWeekEdit = (rowIndex: number, quarterName: string, weekName: string, newValue: number) => {
        // Validate: Don't allow negative values
        if (newValue < 0) {
            return;
        }

        const updatedRows = this.state.rows.map((r, idx) => {
            if (idx === rowIndex) {
                // Deep copy the row we're editing
                return {
                    ...r,
                    quarters: JSON.parse(JSON.stringify(r.quarters)),
                    actions: { ...r.actions }
                };
            }
            return r;
        });
        const row = updatedRows[rowIndex];

        const quarter = row.quarters?.find(q => q.name === quarterName);
        if (!quarter || !quarter.weeks) return;

        const week = quarter.weeks.find(w => w.name === weekName);
        if (!week) return;

        week.value = newValue;
        row.isQuarterEdited = true;
        row.isEdited = true;

        // Recalculate Plan RR based on updated weeks
        row.actions.planRR = this.calculatePlanRR(row);

        this.setState({
            rows: updatedRows,
            hasUnsavedEdits: true
        });
    };

    // Handle drag start for Historic RR
    handleHistoricRRDragStart = (e: React.DragEvent, rowIndex: number, value: number) => {
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('historicRR', value.toString());
        e.dataTransfer.setData('rowIndex', rowIndex.toString());
    };

    // Handle drop on Plan RR
    handlePlanRRDrop = (e: React.DragEvent, rowIndex: number) => {
        e.preventDefault();
        const historicRRValue = parseFloat(e.dataTransfer.getData('historicRR'));

        if (!isNaN(historicRRValue)) {
            this.handlePlanRREdit(rowIndex, historicRRValue);
        }
    };

    // Copy all Historical RR to Plan RR for all rows
    copyAllHistoricalRRToPlanRR = () => {
        const updatedRows = this.state.rows.map((row, idx) => {
            // Calculate Historical RR for this row
            const historicalRR = this.calculateHistoricRR(row);

            // Deep copy the row
            const updatedRow = {
                ...row,
                quarters: JSON.parse(JSON.stringify(row.quarters)),
                actions: { ...row.actions }
            };

            const futureQuarters = this.state.quarters.filter(q => !q.isPast);

            // Distribute quarter-wise (each quarter independently)
            futureQuarters.forEach(quarterDef => {
                const rowQuarter = updatedRow.quarters?.find(q => q.name === quarterDef.name);
                const originalQuarter = updatedRow.originalQuarters?.find(q => q.name === quarterDef.name);
                if (!rowQuarter || !originalQuarter) return;

                // Count future weeks in this quarter
                let futureWeeksInQuarter = 0;
                rowQuarter.weeks.forEach(week => {
                    if (!week.isPast) {
                        futureWeeksInQuarter++;
                    }
                });

                if (futureWeeksInQuarter === 0) return;

                // Calculate quarter total = historicalRR × futureWeeksInQuarter
                const quarterTotal = historicalRR * futureWeeksInQuarter;

                // Calculate old quarter total from ORIGINAL values in THIS quarter only
                let oldQuarterTotal = 0;
                originalQuarter.weeks.forEach(week => {
                    if (!week.isPast) {
                        oldQuarterTotal += week.value || 0;
                    }
                });

                // Collect future weeks in this quarter for rounding adjustment
                const futureWeeksInThisQuarter: { week: any; weekIdx: number }[] = [];
                rowQuarter.weeks.forEach((week, weekIdx) => {
                    if (!week.isPast) {
                        futureWeeksInThisQuarter.push({ week, weekIdx });
                    }
                });

                // Distribute within this quarter
                let distributedInQuarter = 0;

                futureWeeksInThisQuarter.forEach(({ week, weekIdx }) => {
                    const originalValue = originalQuarter.weeks[weekIdx]?.value || 0;

                    // If all weeks in quarter are 0: equal distribution
                    // Otherwise: proportional distribution (zeros naturally get 0)
                    const percentage = oldQuarterTotal !== 0
                        ? (originalValue / oldQuarterTotal)
                        : (1 / futureWeeksInQuarter);

                    const newValue = Math.round(percentage * quarterTotal);
                    week.value = newValue;
                    distributedInQuarter += newValue;
                });

                // Adjust last week in quarter for rounding errors
                const difference = quarterTotal - distributedInQuarter;
                if (difference !== 0 && futureWeeksInThisQuarter.length > 0) {
                    const lastWeek = futureWeeksInThisQuarter[futureWeeksInThisQuarter.length - 1].week;
                    lastWeek.value += difference;
                }
            });

            // Update Plan RR
            updatedRow.actions.planRR = historicalRR;
            updatedRow.isPlanRREdited = true;
            updatedRow.isEdited = true;

            return updatedRow;
        });

        this.setState({
            rows: updatedRows,
            hasUnsavedEdits: true
        });
    };

    // Handle NPI icon click - open SKU assignment modal
    handleNPIClick = (rowIndex: number) => {
        this.setState({
            isSKUAssignmentModalOpen: true,
            selectedRowForAssignment: rowIndex
        });
    };

    // Toggle SKU assignment modal
    toggleSKUAssignmentModal = () => {
        this.setState({
            isSKUAssignmentModalOpen: false,
            selectedRowForAssignment: null
        });
    };

    // Toggle success modal
    toggleSuccessModal = () => {
        this.setState({
            isSuccessModalOpen: false,
            successModalMessage: ''
        });
    };

    // Toggle color legend modal
    toggleColorLegendModal = () => {
        this.setState({
            isColorLegendModalOpen: !this.state.isColorLegendModalOpen
        });
    };

    // Handle SKU assignment save - copy predecessor SKU data
    handleSKUAssignmentSave = () => {
        const rowIndex = this.state.selectedRowForAssignment;

        if (rowIndex === null) {
            alert('No row selected');
            return;
        }

        const clickedRow = this.state.rows[rowIndex];
        const predecessorSku = clickedRow.attributes?.prod_attribute2;

        if (!predecessorSku) {
            alert('No predecessor SKU found');
            this.toggleSKUAssignmentModal();
            return;
        }

        // Find the row with matching SKU (the predecessor)
        const predecessorRow = this.state.rows.find(row => row.sku === predecessorSku);

        if (!predecessorRow) {
            alert(`Predecessor SKU "${predecessorSku}" not found in current data`);
            this.toggleSKUAssignmentModal();
            return;
        }

        // Get current and future quarters (non-past quarters)
        const futureQuarters = this.state.quarters.filter(q => !q.isPast);

        // Build quarters payload from predecessor row
        const quartersPayload: { name: string; key: string; weeks: { name: string; value: number; }[] }[] = [];

        futureQuarters.forEach(quarterDef => {
            const quarter = predecessorRow.quarters?.find(q => q.name === quarterDef.name);
            if (!quarter) return;

            // Filter out past weeks (only include non-past weeks)
            const nonPastWeeks = quarter.weeks?.filter(week => !week.isPast).map(week => {
                const value = week.value || 0;
                const planAsp = week.planAsp || 0;
                const oldValue = week.oldValue || 0;

                const oldSalesValue = week.salesValue || 0;
                const oldPlanMargin = week.planMargin || 0;

                // Recalculated values
                const newSalesValue = parseFloat((value * planAsp).toFixed(2));

                const oldPlanMarginPct =
                    oldSalesValue === 0
                        ? 0
                        : parseFloat(((oldPlanMargin / oldSalesValue) * 100).toFixed(2));

                const newPlanMargin = parseFloat(
                    (newSalesValue * (oldPlanMarginPct / 100)).toFixed(2)
                );

                // Incremental values
                const incrementalValue = value - oldValue;
                const incrementalSalesValue = newSalesValue - oldSalesValue;
                const incrementalPlanMargin = newPlanMargin - oldPlanMargin;

                return {
                    name: week.name,
                    value: week.value || 0,
                    salesValue: newSalesValue || 0,
                    planAsp: parseFloat(week.planAsp.toFixed(2)) || 0,
                    planMargin: newPlanMargin || 0,
                    incrmentalValue: incrementalValue || 0,
                    incrementalSalesValue: incrementalSalesValue || 0,
                    incrementalPlanMargin: incrementalPlanMargin || 0
                };
            }) || [];


            // Only include quarter if it has non-past weeks
            if (nonPastWeeks.length > 0) {
                quartersPayload.push({
                    name: quarter.name,
                    key: quarter.key || quarterDef.key || '',
                    weeks: nonPastWeeks
                });
            }
        });

        // Create payload
        const payload: INPICopyPredecessorPayload = {
            predecessorSku: predecessorSku,
            sku: clickedRow.sku,
            quarters: quartersPayload
        };

        const params = {
            userId: sessionStorage.getItem('userName'),
            planId: GetPlanId(this.props.history.location.pathname)
        };

        // Close modal before API call
        this.toggleSKUAssignmentModal();

        this.setState({ isLoading: true });

        NPICopyPredecessorSku(payload, params)
            .then((response: any) => {
                this.setState({ isLoading: false });
                if (response) {
                    const message = response?.message || 'Predecessor SKU Data Copied and Freezed Successfully.';
                    this.setState({
                        isSuccessModalOpen: true,
                        successModalMessage: message
                    });
                    // Refresh grid data to show updated values
                    this.getRunRatePlanGridData();
                }
            })
            .catch((error) => {
                console.error('Error copying NPI data:', error);
                const errorMessage = error?.message || 'Error While Copying Predecessor SKU Data !!';
                this.setState({
                    isLoading: false,
                    isSuccessModalOpen: true,
                    successModalMessage: errorMessage
                });
            });
    };

    // Handle EOL icon click - New EOL calculation with 3 scenarios
    handleEOLClick = (rowIndex: number) => {
        const row = this.state.rows[rowIndex];
        const stock = row.attributes?.stock_onhand_qty || 0;
        const futureIntake = row.attributes?.incoming_inv_qty || 0;
        const availableInventory = stock + futureIntake;

        // Get all future quarters and weeks
        const futureQuarters = this.state.quarters.filter(q => !q.isPast);
        const futureWeeks: { quarter: any; week: any; weekIdx: number }[] = [];
        let sumOfFutureWeeks = 0;

        futureQuarters.forEach(quarterDef => {
            const rowQuarter = row.quarters?.find(q => q.name === quarterDef.name);
            if (!rowQuarter) return;

            rowQuarter.weeks.forEach((week, weekIdx) => {
                if (!week.isPast) {
                    futureWeeks.push({ quarter: rowQuarter, week, weekIdx });
                    sumOfFutureWeeks += (week.value || 0);
                }
            });
        });

        // Case 1: Available inventory > sum of future weeks
        if (availableInventory > sumOfFutureWeeks) {
            this.setState({
                isSuccessModalOpen: true,
                successModalMessage: `Available inventory (${availableInventory}) exceeds the sum of all future weeks (${sumOfFutureWeeks}). Please adjust the weekly values.`
            });
            return;
        }

        // Case 2 & 3: Available inventory <= sum of future weeks
        // Distribute week by week until inventory runs out
        const updatedRows = this.state.rows.map((r, idx) => {
            if (idx === rowIndex) {
                return {
                    ...r,
                    quarters: JSON.parse(JSON.stringify(r.quarters)),
                    actions: { ...r.actions }
                };
            }
            return r;
        });

        const updatedRow = updatedRows[rowIndex];
        let cumulativeSum = 0;

        // Get the updated future quarters from the cloned row
        futureQuarters.forEach(quarterDef => {
            const rowQuarter = updatedRow.quarters?.find(q => q.name === quarterDef.name);
            if (!rowQuarter) return;

            rowQuarter.weeks.forEach((week) => {
                if (!week.isPast) {
                    const currentWeekValue = week.value || 0;

                    if (cumulativeSum >= availableInventory) {
                        // Already reached/exceeded available inventory, set rest to 0
                        week.value = 0;
                    } else if (cumulativeSum + currentWeekValue <= availableInventory) {
                        // Can accommodate full week value
                        cumulativeSum += currentWeekValue;
                        // Keep week.value as is
                    } else {
                        // Partial week - adjust to reach exactly available inventory
                        week.value = availableInventory - cumulativeSum;
                        cumulativeSum = availableInventory;
                    }
                }
            });
        });

        updatedRow.isPlanRREdited = true;
        updatedRow.isEdited = true;

        // Recalculate Plan RR
        updatedRow.actions.planRR = this.calculatePlanRR(updatedRow);

        this.setState({
            rows: updatedRows,
            hasUnsavedEdits: true
        });
    };

    // Handle EOL header click - apply new EOL logic to all rows
    handleEOLHeaderClick = () => {
        const futureQuarters = this.state.quarters.filter(q => !q.isPast);
        let hasChanges = false;
        let hasWarnings = false;
        let warningCount = 0;

        // Deep copy rows to avoid state mutation
        const updatedRows = this.state.rows.map(r => ({
            ...r,
            quarters: JSON.parse(JSON.stringify(r.quarters)),
            actions: { ...r.actions }
        }));

        // Apply EOL calculation to all rows where stock values are not both 0
        updatedRows.forEach((row, rowIndex) => {
            const stock = row.attributes?.stock_onhand_qty || 0;
            const incoming = row.attributes?.incoming_inv_qty || 0;

            // Skip if both stock and incoming are 0
            if (stock === 0 && incoming === 0) {
                return;
            }

            const availableInventory = stock + incoming;
            let sumOfFutureWeeks = 0;

            // Calculate sum of all future weeks
            futureQuarters.forEach(quarterDef => {
                const rowQuarter = row.quarters?.find(q => q.name === quarterDef.name);
                if (!rowQuarter) return;

                rowQuarter.weeks.forEach((week) => {
                    if (!week.isPast) {
                        sumOfFutureWeeks += (week.value || 0);
                    }
                });
            });

            // Case 1: Available inventory > sum of future weeks - skip row with warning
            if (availableInventory > sumOfFutureWeeks) {
                hasWarnings = true;
                warningCount++;
                return;
            }

            // Case 2 & 3: Available inventory <= sum of future weeks
            // Distribute week by week until inventory runs out
            let cumulativeSum = 0;

            futureQuarters.forEach(quarterDef => {
                const rowQuarter = row.quarters?.find(q => q.name === quarterDef.name);
                if (!rowQuarter) return;

                rowQuarter.weeks.forEach((week) => {
                    if (!week.isPast) {
                        const currentWeekValue = week.value || 0;

                        if (cumulativeSum >= availableInventory) {
                            // Already reached/exceeded available inventory, set rest to 0
                            week.value = 0;
                        } else if (cumulativeSum + currentWeekValue <= availableInventory) {
                            // Can accommodate full week value
                            cumulativeSum += currentWeekValue;
                            // Keep week.value as is
                        } else {
                            // Partial week - adjust to reach exactly available inventory
                            week.value = availableInventory - cumulativeSum;
                            cumulativeSum = availableInventory;
                        }
                    }
                });
            });

            // Update Plan RR
            row.actions.planRR = this.calculatePlanRR(row);
            row.isPlanRREdited = true;
            row.isEdited = true;
            hasChanges = true;
        });

        // Update state once with all changes
        if (hasChanges) {
            this.setState({
                rows: updatedRows,
                hasUnsavedEdits: true
            }, () => {
                // Show warning message if any rows were skipped
                if (hasWarnings) {
                    this.setState({
                        isSuccessModalOpen: true,
                        successModalMessage: `EOL applied to eligible rows. ${warningCount} row(s) skipped where available inventory exceeds the sum of future weeks. Please adjust those manually.`
                    });
                }
            });
        } else if (hasWarnings) {
            // Only warnings, no changes
            this.setState({
                isSuccessModalOpen: true,
                successModalMessage: `${warningCount} row(s) have available inventory exceeding the sum of future weeks. Please adjust the weekly values manually.`
            });
        }
    };

    // Handle cancel - reset to original order and edits
    handleCancel = () => {
        // Deep copy originalRows to reset all data and edits
        const resetRows = JSON.parse(JSON.stringify(this.state.originalRows));

        // Reset all edit flags and restore original quarter values
        resetRows.forEach((row: any) => {
            row.isPlanRREdited = false;
            row.isQuarterEdited = false;
            row.isEdited = false;

            // Restore quarters from originalQuarters (this preserves the distribution logic)
            if (row.originalQuarters) {
                row.quarters = JSON.parse(JSON.stringify(row.originalQuarters));

                // Restore oldValue references for future edits
                const futureQuarters = this.state.quarters.filter(q => !q.isPast);
                futureQuarters.forEach(quarterDef => {
                    const quarter = row.quarters?.find(q => q.name === quarterDef.name);
                    if (quarter && quarter.weeks) {
                        quarter.weeks.forEach((week: any) => {
                            if (!week.isPast) {
                                week.oldValue = week.value;
                            }
                        });
                    }
                });
            }

            // Recalculate Plan RR based on restored quarter values
            if (row.actions) {
                row.actions.planRR = this.calculatePlanRR(row);
            }
        });

        this.setState({
            rows: resetRows,
            hasSequenceChanged: false,
            hasUnsavedEdits: false,
            isEditable: false,
        }, () => {
            // Re-apply filters to rebuild rows from reset data
            this.applyFiltersAndPagination();
        });
    };

    // Handle save and freeze - save and mark as frozen
    handleSaveAndFreeze = () => {
        // Filter only edited rows
        const editedRows = this.state.rows.filter((row: any) => row.isEdited === true);

        if (editedRows.length === 0) {
            alert('No changes to save.');
            return;
        }

        // Prepare the payload for edited rows only
        const payload: ISaveAndFreezePayload[] = editedRows.map((row: any) => {
            // Get quarters with edited weeks only
            const quartersWithEditedWeeks = (row.quarters || [])
                .filter((quarter) => !quarter.isPast)
                .map((quarter: any) => {
                    // Find original quarter to compare
                    const originalQuarter = row.originalQuarters?.find((oq: any) => oq.name === quarter.name);

                    // Filter weeks that have been edited
                    const editedWeeks = (quarter.weeks || [])
                        .filter((week) => !week.isPast)
                        .filter((week: any) => {
                            // If no original quarter exists, include all weeks
                            if (!originalQuarter) return true;

                            // Find corresponding original week
                            const originalWeek = originalQuarter.weeks?.find((ow: any) => ow.name === week.name);

                            // If no original week exists, include this week
                            if (!originalWeek) return true;

                            // Check if week values have changed
                            return week.value !== originalWeek.value ||
                                   week.planAsp !== originalWeek.planAsp;
                        })
                        .map((week: any) => {
                            const oldSalesValue: number = week.salesValue;
                            const newSalesValue: number = parseFloat((week.value * week.planAsp).toFixed(2));

                            const oldPlanMargin: number = week.planMargin;
                            const oldPlanMarginPct: number = oldSalesValue === 0 ? 0 : parseFloat(((oldPlanMargin / oldSalesValue) * 100).toFixed(2));

                            const newPlanMargin: number = parseFloat((newSalesValue * (oldPlanMarginPct / 100)).toFixed(2));
                            // Calculate Incremental Values
                            const incrementalValue: number = week.value - (week.oldValue || 0);
                            const incrmentalSalesValue: number = newSalesValue - oldSalesValue;
                            const incrmentalPlanMargin: number = newPlanMargin - oldPlanMargin;
                            return {
                                name: week.name,
                                value: week.value || 0,
                                salesValue: newSalesValue || 0,
                                planAsp: parseFloat(week.planAsp.toFixed(2)) || 0,
                                planMargin: newPlanMargin || 0,
                                incrmentalValue: incrementalValue || 0,
                                incrmentalSalesValue,
                                incrmentalPlanMargin,
                            };
                        });

                    return {
                        name: quarter.name,
                        key: quarter.key,
                        weeks: editedWeeks
                    };
                })
                .filter((quarter: any) => quarter.weeks.length > 0); // Only include quarters with edited weeks

            return {
                sku: row.sku,
                quarters: quartersWithEditedWeeks
            };
        });

        const params = {
            userId: sessionStorage.getItem('userName'),
            planId: GetPlanId(this.props.history.location.pathname)
        };

        this.setState({ isLoading: true });

        SaveAndFreezePlan(payload, params)
            .then((response: any) => {
                // Update original quarter values to current values after save
                const updatedRows = this.state.rows.map(row => {
                    if (row.isEdited) {
                        return {
                            ...row,
                            originalQuarters: JSON.parse(JSON.stringify(row.quarters)),
                            isPlanRREdited: false,
                            isQuarterEdited: false,
                            isEdited: false
                        };
                    }
                    return row;
                });

                // Show success modal with API message
                const message = response?.message || 'Run Rate Plan saved and freezed successfully.';
                this.setState({
                    isLoading: false,
                    isSuccessModalOpen: true,
                    isEditable: false,
                    hasUnsavedEdits: false,
                    successModalMessage: message
                }, () => {
                    this.getRunRatePlanGridData();
                });
            })
            .catch((error) => {
                console.error('Error saving and freezing data:', error);
                // Show error modal with error message
                const errorMessage = error?.message || 'Error saving data. Please try again.';
                this.setState({
                    isLoading: false,
                    isSuccessModalOpen: true,
                    successModalMessage: errorMessage
                });
            });
    };

    // Handle Export Plan RR
    handleExportPlanRR = () => {
        const params = {
            userId: sessionStorage.getItem('userName'),
            planId: GetPlanId(this.props.history.location.pathname)
        };

        // Get the selected number of historic weeks
        const selectedHistoricWeeks = this.state.selectedHistoricWeeks;

        // Build the payload with historic weeks data for ALL SKUs (use originalRows, not rows)
        const payload: IExportPlanRRPayload[] = this.state.originalRows.map((row: any) => {
            // Calculate Historical RR
            const historicalRR = this.calculateHistoricRR(row);

            // Calculate Plan RR
            const planRR = this.calculatePlanRR(row);

            // Collect the last N weeks from past quarters
            const weeks: { name: string; value: number }[] = [];

            // Get all past quarters
            const pastQuarters = this.state.quarters.filter(q => q.isPast);

            // Collect all weeks from past quarters
            const allPastWeeks: { name: string; value: number }[] = [];
            pastQuarters.forEach(quarterDef => {
                const rowQuarter = row.quarters?.find(q => q.name === quarterDef.name);
                if (rowQuarter && rowQuarter.weeks) {
                    rowQuarter.weeks.forEach(week => {
                        allPastWeeks.push({
                            name: week.name,
                            value: week.value || 0
                        });
                    });
                }
            });

            // Get the last N weeks based on selectedHistoricWeeks
            const lastNWeeks = allPastWeeks.slice(-selectedHistoricWeeks);
            weeks.push(...lastNWeeks);

            return {
                sku: row.sku,
                historicalRR: historicalRR,
                planRR: planRR,
                weeks: weeks
            };
        });

        this.setState({ isLoading: true });

        ExportPlanRR(payload, params)
            .then((res: any) => {
                this.setState({ isLoading: false });
                if (res) {
                    // Create a download link for the exported file
                    res.blob().then(blob => {
                        let url = window.URL.createObjectURL(blob);
                        let a = document.createElement('a');
                        a.href = url;
                        a.download = `plan-rr-${params.planId}.xlsx`;
                        a.click();
                    });
                }
            })
            .catch((error) => {
                this.setState({ isLoading: false });
                console.error('Error exporting Plan RR:', error);
            });
    };

    // Handle Export Weight Measure
    handleExportWeightMeasure = () => {
        const params = {
            userId: sessionStorage.getItem('userName'),
            planId: GetPlanId(this.props.history.location.pathname)
        };

        this.setState({ isLoading: true });

        ExportWeightMeasure(params)
            .then((res: any) => {
                this.setState({ isLoading: false });
                if (res) {
                    // Create a download link for the exported file
                    res.blob().then(blob => {
                        let url = window.URL.createObjectURL(blob);
                        let a = document.createElement('a');
                        a.href = url;
                        a.download = `weight-measure-${params.planId}.xlsx`;
                        a.click();
                    });
                }
            })
            .catch((error) => {
                this.setState({ isLoading: false });
                console.error('Error exporting Weight Measure:', error);
            });
    };

    // Handle Import Plan RR
    handleImportPlanRR = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const params = {
            userId: sessionStorage.getItem('userName'),
            planId: GetPlanId(this.props.history.location.pathname)
        };

        // Create FormData to upload the file
        const formData = new FormData();
        formData.append('file', file);

        this.setState({ isLoading: true });

        ImportPlanRR(formData, params)
            .then((res: any) => {
                this.setState({ isLoading: false });
                if (res) {
                    // Show success modal with API message
                    const message = res?.message || 'Imported Plan RR Data Saved and Freezed Successfully.';
                    this.setState({
                        isLoading: false,
                        isSuccessModalOpen: true,
                        successModalMessage: message
                    }, () => {
                        // Refresh the grid data after successful import
                        this.getRunRatePlanGridData();
                    });
                }
            })
            .catch((error) => {
                const errorMessage = error?.message || 'Error While Importing Plan RR Data !!';
                this.setState({
                    isLoading: false,
                    isSuccessModalOpen: true,
                    successModalMessage: errorMessage
                });
                console.error('Error While Importing Plan RR Data:', error);
            });

        // Reset the input value to allow re-uploading the same file
        e.target.value = '';
    };

    // Handle Import Weight Measure
    handleImportWeightMeasure = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const params = {
            userId: sessionStorage.getItem('userName'),
            planId: GetPlanId(this.props.history.location.pathname)
        };

        // Create FormData to upload the file
        const formData = new FormData();
        formData.append('file', file);

        this.setState({ isLoading: true });

        ImportWeightMeasure(formData, params)
            .then((res: any) => {
                this.setState({ isLoading: false });
                if (res) {
                    const message = res;
                    this.setState({
                    isLoading: false,
                    isSuccessModalOpen: true,
                    successModalMessage: message
                }, () => {
                        // Refresh the grid data after successful import
                        this.getRunRatePlanGridData();
                    });
                }
            })
            .catch((error) => {
                this.setState({ isLoading: false });
                console.error('Error importing Weight Measure:', error);
            });

        // Reset the input value to allow re-uploading the same file
        e.target.value = '';
    };

    render(): React.ReactNode {
        // Separate past and future quarters
        const pastQuarters = this.state.quarters.filter(q => q.isPast);
        const futureQuarters = this.state.quarters.filter(q => !q.isPast);

        if (this.state.isAutoLoad) {
            return <InitialMessage />;
        }

        const headerSection = <Card className="section-header-card">
            <Row>
                <Col md={5}>
                    <div className="custom-form-container">
                        <label htmlFor="historic-weeks" className="custom-form-label">Historic RR Weeks</label>
                        <select
                            id="historic-weeks"
                            className="custom-drop-down-field"
                            value={this.state.selectedHistoricWeeks}
                            onChange={(e) => this.handleHistoricChange(parseInt(e.target.value), this.state.oldHistoricViewTypeSelection?this.state.oldHistoricViewTypeSelection:'RR only')}
                        >
                            {[3, 6, 13].map(weeks => (
                                <option key={weeks} value={weeks}>
                                    {weeks}
                                </option>
                            ))}
                        </select>
                    
                        <label htmlFor="historic-weeks-view-type" className="custom-form-label">Historic View Type</label>
                        <select
                            id="historic-weeks-view-type"
                            className="custom-drop-down-field"
                            value={this.state.selectedHisticViewType}
                            onChange={(e) => this.handleHistoricChange(this.state.selectedHistoricWeeks, e.target.value)}
                        >
                            {['RR only', 'Deals incl'].map(histType => (
                                <option key={histType} value={histType}>
                                    {histType}
                                </option>
                            ))}
                        </select>    
                    </div>
                </Col>
                

                <Col md={5}></Col>

                <Col md={0} className="text-right d-flex justify-content-end align-items-center">
                    <Tooltip
                        content={'Color Legend'}
                        direction={'top'}
                    >
                        <i
                            className="ri-information-line font-size-18 cursor-pointer px-2"
                            style={{ verticalAlign: 'middle' }}
                            onClick={this.toggleColorLegendModal}
                        />
                    </Tooltip>

                    <Tooltip
                        content={'Copy Historical RR to Plan RR'}
                        direction={'top'}
                    >
                        <i
                            className="ri-file-copy-line font-size-18 cursor-pointer px-2"
                            style={{ verticalAlign: 'middle' }}
                            onClick={this.copyAllHistoricalRRToPlanRR}
                        />
                    </Tooltip>

                    <Tooltip
                        content={'Save Row Sequence'}
                        direction={'top'}
                    >
                        <i
                            className="ri-drag-drop-line font-size-18 cursor-pointer px-2"
                            style={{ verticalAlign: 'middle' }}
                            onClick={this.saveSkuSequence}
                        />
                    </Tooltip>

                    <Tooltip
                        content={'Save Column Sequence And Width'}
                        direction={'top'}
                    >
                        <i
                            className="ri-drag-drop-line font-size-18 cursor-pointer px-2"
                            style={{ verticalAlign: 'middle' }}
                            onClick={this.saveMeasureSequence}
                        />
                    </Tooltip>

                    <Dropdown
                        isOpen={this.state.isVisibilityMenuOpened}
                        toggle={this.toggleVisibilityMenu}
                        className="d-inline-block user-dropdown"
                    >
                        <Tooltip
                            content={'Measure Visibility Setting'}
                            direction={'top'}
                        >
                            <DropdownToggle tag="button" className="btn action-btn waves-effect p-0">
                                <i className="ri-list-settings-line font-size-18" />
                            </DropdownToggle>
                        </Tooltip>

                        <DropdownMenu className="dropdown-menu-end"
                                      style={{ minWidth: '300px', maxHeight: '400px', overflowY: 'auto' }}>
                            <div className="p-3">
                                <h6 className="mb-3">Attribute Column Visibility</h6>
                                {this.state.measureVisibilityList.map((measure, idx) => (
                                    <div key={idx} className="mb-2">
                                        <div className="form-check">
                                            <input
                                                id={`visibility-${idx}`}
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={measure.isVisible}
                                                onChange={() => this.onUpdateVisibilityCheck(idx)}
                                            />
                                            <label htmlFor={`visibility-${idx}`}
                                                   className="form-check-label cursor-pointer">
                                                {measure.header}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                                <div className="mt-3 text-end">
                                    <Button
                                        color="primary"
                                        size="sm"
                                        disabled={this.checkVisibilitySaveButton()}
                                        onClick={this.updateMeasureVisibility}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </div>
                        </DropdownMenu>
                    </Dropdown>

                    <Tooltip
                        content={'Edit measures'}
                        direction={'top'}
                    >
                        <div
                            className="action-btn"
                            onClick={() => this.setState({ isEditable: !this.state.isEditable })}
                        >
                            <img src={EditIcon} alt="edit-icon" className="edit-icon-position" />
                        </div>
                    </Tooltip>

                    <Dropdown
                        isOpen={this.state.isMoreMenuOpened}
                        toggle={this.toggleMoreMenu}
                        className="d-inline-block user-dropdown"
                    >
                        <Tooltip content={'More'} direction={'top'}>
                            <DropdownToggle tag="button" className="btn action-btn waves-effect p-0">
                                <img src={MoreIcon} alt="more-icon" />
                            </DropdownToggle>
                        </Tooltip>

                        <DropdownMenu className="dropdown-menu-end">
                            {/* Export with sub-options */}
                            <Dropdown
                                isOpen={this.state.isExportMenuOpened}
                                toggle={this.toggleExportMenu}
                                className="d-inline-block user-dropdown"
                                direction="end"
                            >
                                <DropdownToggle className={styles['dropdown-toggle']}>
                                    Download Template
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem onClick={this.handleExportPlanRR}>
                                        Plan RR
                                    </DropdownItem>
                                    <DropdownItem onClick={this.handleExportWeightMeasure}>
                                        Weight Measure
                                    </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>

                            <DropdownItem divider />

                            {/* Import with sub-options */}
                            <Dropdown
                                isOpen={this.state.isImportMenuOpened}
                                toggle={this.toggleImportMenu}
                                className="d-inline-block user-dropdown"
                                direction="end"
                            >
                                <DropdownToggle className={styles['dropdown-toggle']}>
                                    Import
                                </DropdownToggle>
                                <DropdownMenu>
                                    <div className="position-relative">
                                        <DropdownItem>Plan RR</DropdownItem>
                                        <input
                                            type="file"
                                            accept=".csv,.xlsx,.xls"
                                            onChange={this.handleImportPlanRR}
                                        />
                                    </div>
                                    <div className="position-relative">
                                        <DropdownItem>Weight Measure</DropdownItem>
                                        <input
                                            type="file"
                                            accept=".csv,.xlsx,.xls"
                                            onChange={this.handleImportWeightMeasure}
                                        />
                                    </div>
                                </DropdownMenu>
                            </Dropdown>
                        </DropdownMenu>
                    </Dropdown>
                </Col>
            </Row>
        </Card>;

        const bodySection = <div className={`table-responsive custom-table ${styles['run-rate-table-container']}`}>
            <table className="table table-bordered mb-0">
                <thead>
                {/* Main header row */}
                <tr>
                    {/* Render fixed columns */}
                    {this.state.cols.map((group, groupIdx) => {
                        if (group.groupKey === 'fixed') {
                            return group.columns.map((col, colIdx) => (
                                <th
                                    key={`${groupIdx}-${colIdx}`}
                                    rowSpan={col.dataType === 'icon' ? 3 : 2}
                                    className={`text-center text-nowrap ${this.getStickyClass(col, group.groupKey, true)}`}
                                    style={{ cursor: (col.dataType !== 'icon' && col.key === 'eol') ? 'pointer' : 'default' }}
                                    onClick={() => {
                                        /* Sorting functionality disabled
                                        if (col.dataType !== 'icon') {
                                            this.handleSort(col.key);
                                        } else */ if (col.key === 'eol') {
                                            this.handleEOLHeaderClick();
                                        }
                                    }}
                                >
                                    {col.header}
                                    {/* Sorting icon - COMMENTED OUT - sorting functionality disabled
                                    {col.dataType !== 'icon' && this.state.sortKey === col.key && (
                                        <i className={`ri-arrow-${this.state.sortDirection === 'asc' ? 'up' : 'down'}-s-line ms-1`} />
                                    )} */}
                                    {col.key === 'eol' && (
                                        <div>
                                            <i className="ri-subtract-line text-danger font-size-16 cursor-pointer"
                                               style={{ cursor: 'pointer' }}
                                               title="Apply EOL to all rows" />
                                        </div>
                                    )}
                                </th>
                            ));
                        }
                        return null;
                    })}

                    {/* Render attribute columns */}
                    {this.state.cols.map((group, groupIdx) => {
                        if (group.groupKey === 'fixed' || group.groupKey === 'actions') return null;

                        if (group.expandable) {
                            // Count visible columns only
                            const visibleColumnsCount = this.state.attributeColumns.filter(col => col.isVisible !== false).length;
                            return (
                                <th
                                    key={groupIdx}
                                    colSpan={this.state.isAttributesExpanded ? visibleColumnsCount : 1}
                                    className="text-center"
                                >
                                    <span className="toggle-col">
                                        {group.groupName}
                                        <i
                                            className={`${this.state.isAttributesExpanded ? 'ri-checkbox-indeterminate-fill' : 'ri-add-box-fill'} ms-sm-2 cursor-pointer`}
                                            onClick={this.toggleAttributes}
                                        />
                                    </span>
                                </th>
                            );
                        }

                        return null;
                    })}

                    {/* Render past quarter headers BEFORE actions */}
                    {pastQuarters.map((quarter, qIdx) => {
                        const isExpanded = this.state.expandedQuarters[quarter.name];
                        return (
                            <th
                                key={`past-quarter-${qIdx}`}
                                colSpan={isExpanded ? (quarter.weekNames?.length || 1) : 1}
                                className="text-center"
                            >
                                {quarter.isExpandable ? (
                                    <span className="toggle-col">
                                        {quarter.name}
                                        <i
                                            className={`${isExpanded ? 'ri-checkbox-indeterminate-fill' : 'ri-add-box-fill'} ms-sm-2 cursor-pointer`}
                                            onClick={() => this.toggleQuarter(quarter.name)}
                                        />
                                    </span>
                                ) : (
                                    <span>{quarter.name}</span>
                                )}
                            </th>
                        );
                    })}

                    {/* Render actions column */}
                    {this.state.cols.map((group, groupIdx) => {
                        if (group.groupKey === 'actions') {
                            return (
                                <th
                                    key={groupIdx}
                                    colSpan={group.columns.length}
                                    className="text-center text-nowrap"
                                >
                                    {group.groupName}
                                </th>
                            );
                        }
                        return null;
                    })}

                    {/* Render future quarter headers AFTER actions */}
                    {futureQuarters.map((quarter, qIdx) => {
                        const isExpanded = this.state.expandedQuarters[quarter.name];
                        return (
                            <th
                                key={`future-quarter-${qIdx}`}
                                colSpan={isExpanded ? (quarter.weekNames?.length || 1) : 1}
                                className="text-center"
                            >
                                {quarter.isExpandable ? (
                                    <span className="toggle-col">
                                        {quarter.name}
                                        <i
                                            className={`${isExpanded ? 'ri-checkbox-indeterminate-fill' : 'ri-add-box-fill'} ms-sm-2 cursor-pointer`}
                                            onClick={() => this.toggleQuarter(quarter.name)}
                                        />
                                    </span>
                                ) : (
                                    <span>{quarter.name}</span>
                                )}
                            </th>
                        );
                    })}
                </tr>

                {/* Sub-header row */}
                <tr>
                    {/* Render attribute column sub-headers */}
                    {this.state.isAttributesExpanded ? (
                        this.state.attributeColumns
                            .filter(col => col.isVisible !== false)
                            .map((col, colIdx) => (
                                <th
                                    key={`attr-${colIdx}`}
                                    className={`text-nowrap ${this.state.draggedAttributeColumn === colIdx ? 'bg-light' : ''}`}
                                    style={{
                                        cursor: 'move',
                                        position: 'relative',
                                        minWidth: `${this.state.columnWidths[col.key] || 60}px`,
                                        maxWidth: `${this.state.columnWidths[col.key] || 60}px`,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                    draggable
                                    onDragStart={(e) => this.handleAttributeColumnDragStart(e, colIdx)}
                                    onDragOver={(e) => this.handleAttributeColumnDragOver(e, colIdx)}
                                    onDrop={(e) => this.handleAttributeColumnDrop(e, colIdx)}
                                    onDragEnd={this.handleAttributeColumnDragEnd}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                        /* Sorting functionality disabled - onClick={() => this.handleSort(col.key)} */
                                    >
                                        <span>{col.header}</span>
                                        {/* Sorting icon - COMMENTED OUT - sorting functionality disabled
                                        {this.state.sortKey === col.key && (
                                            <i className={`ri-arrow-${this.state.sortDirection === 'asc' ? 'up' : 'down'}-s-line`} />
                                        )} */}
                                    </div>
                                    <div
                                        className="dragger-line"
                                        onMouseDown={(e) => this.handleColumnResizeStart(col.key, e)}
                                        style={{ cursor: 'col-resize' }}
                                    />
                                </th>
                            ))
                    ) : (
                        // Show first visible column when collapsed
                        this.state.attributeColumns.filter(col => col.isVisible !== false).length > 0 && (
                            <th
                                key="attr-collapsed"
                                className={`text-nowrap`}
                                style={{ cursor: 'default', position: 'relative' }} /* Changed cursor from 'pointer' to 'default' - sorting disabled */
                                /* Sorting functionality disabled - onClick={() => this.handleSort(this.state.attributeColumns.filter(col => col.isVisible !== false)[0].key)} */
                            >
                                {this.state.attributeColumns.filter(col => col.isVisible !== false)[0].header}
                                {/* Sorting icon - COMMENTED OUT - sorting functionality disabled
                                {this.state.sortKey === this.state.attributeColumns.filter(col => col.isVisible !== false)[0].key && (
                                    <i className={`ri-arrow-${this.state.sortDirection === 'asc' ? 'up' : 'down'}-s-line ms-1`} />
                                )} */}
                            </th>
                        )
                    )}

                    {/* Render week sub-headers for PAST quarters */}
                    {pastQuarters.map((quarter, qIdx) => {
                        const isExpanded = this.state.expandedQuarters[quarter.name];
                        const displayWeeks = quarter.weekNames || quarter.weeks || [];

                        if (isExpanded) {
                            return displayWeeks.map((weekName, wIdx) => (
                                <th key={`past-quarter-${qIdx}-week-${wIdx}`} className="text-nowrap"
                                    style={{
                                        backgroundColor: '#0070c0',
                                        color: 'white',
                                        minWidth: '60px',
                                        maxWidth: '60px'
                                    }}>
                                    {this.formatWeekName(weekName)}
                                </th>
                            ));
                        } else {
                            // Show first week when collapsed
                            return (
                                <th key={`past-quarter-${qIdx}-collapsed`} className="text-nowrap"
                                    style={{
                                        backgroundColor: '#0070c0',
                                        color: 'white',
                                        minWidth: '60px',
                                        maxWidth: '60px'
                                    }}>
                                    {this.formatWeekName(displayWeeks[0] || '')}
                                </th>
                            );
                        }
                    })}

                    {/* Render actions column sub-headers */}
                    {this.state.cols.map((group, groupIdx) => {
                        if (group.groupKey === 'actions') {
                            return group.columns.map((col, colIdx) => (
                                <th key={`${groupIdx}-${colIdx}`}
                                    className={`text-nowrap`}
                                    style={col.key === 'historicalRR' || col.key === 'planRR' ? {
                                        backgroundColor: '#0070c0',
                                        color: 'white',
                                        minWidth: col.key === 'historicalRR' ? '100px' : '60px',
                                        maxWidth: col.key === 'historicalRR' ? '100px' : '60px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    } : {
                                        minWidth: '60px',
                                        maxWidth: '60px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                    {col.header}
                                </th>
                            ));
                        }
                        return null;
                    })}

                    {/* Render week sub-headers for FUTURE quarters */}
                    {futureQuarters.map((quarter, qIdx) => {
                        const isExpanded = this.state.expandedQuarters[quarter.name];
                        const displayWeeks = quarter.weekNames || quarter.weeks || [];

                        if (isExpanded) {
                            return displayWeeks.map((weekName, wIdx) => (
                                <th key={`future-quarter-${qIdx}-week-${wIdx}`} className="text-nowrap"
                                    style={{
                                        backgroundColor: '#3c7d22',
                                        color: 'white',
                                        minWidth: '60px',
                                        maxWidth: '60px'
                                    }}>
                                    {this.formatWeekName(weekName)}
                                </th>
                            ));
                        } else {
                            // Show first week when collapsed
                            return (
                                <th key={`future-quarter-${qIdx}-collapsed`} className="text-nowrap"
                                    style={{
                                        backgroundColor: '#3c7d22',
                                        color: 'white',
                                        minWidth: '60px',
                                        maxWidth: '60px'
                                    }}>
                                    {this.formatWeekName(displayWeeks[0] || '')}
                                </th>
                            );
                        }
                    })}
                </tr>

                {/* Filter row */}
                <tr>
                    {/* Filter inputs for fixed columns (SKU and SKU Description only, icons span 3 rows) */}
                    {this.state.cols.map((group, groupIdx) => {
                        if (group.groupKey === 'fixed') {
                            return group.columns.map((col, colIdx) => {
                                // Skip icon columns - they already span 3 rows
                                if (col.dataType === 'icon') return null;

                                return (
                                    <th
                                        key={`filter-${groupIdx}-${colIdx}`}
                                        className={`text-${this.getAlignment(col)} ${this.getStickyClass(col, group.groupKey, true)}`}
                                    >
                                        {col.dataType?.toLowerCase() === 'string' ? (
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder={`Search ${col.header}`}
                                                value={this.state.columnFilters[col.key]?.value || ''}
                                                onChange={(e) => this.updateFilterValue(col.key, e.target.value)}
                                                style={{ fontSize: '10px', padding: '2px 4px' }}
                                            />
                                        ) : (col.dataType === 'number' || col.dataType === 'currency' || col.dataType === 'percentage') ? (
                                            <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={this.state.columnFilters[col.key]?.condition || '='}
                                                    onChange={(e) => this.updateFilterCondition(col.key, e.target.value)}
                                                    style={{
                                                        fontSize: '10px',
                                                        padding: '2px',
                                                        width: '25px',
                                                        appearance: 'none',
                                                        WebkitAppearance: 'none',
                                                        MozAppearance: 'none',
                                                        backgroundImage: 'none'
                                                    }}
                                                >
                                                    <option value="=">=</option>
                                                    <option value="<">&lt;</option>
                                                    <option value=">">&gt;</option>
                                                    <option value="<=">&lt;=</option>
                                                    <option value=">=">&gt;=</option>
                                                </select>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    placeholder="Value"
                                                    value={this.state.columnFilters[col.key]?.value || ''}
                                                    onChange={(e) => this.updateFilterValue(col.key, e.target.value)}
                                                    style={{ fontSize: '10px', padding: '2px 4px', width: '100%', minWidth: '30px' }}
                                                />
                                            </div>
                                        ) : null}
                                    </th>
                                );
                            });
                        }
                        return null;
                    })}

                    {/* Filter inputs for attribute columns */}
                    {this.state.isAttributesExpanded ? (
                        this.state.attributeColumns
                            .filter(col => col.isVisible !== false)
                            .map((col, colIdx) => (
                                <th
                                    key={`filter-attr-${colIdx}`}
                                    className={`text-${this.getAlignment(col)}`}
                                >
                                    {col.dataType === 'string' ? (
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder={`Search ${col.header}`}
                                            value={this.state.columnFilters[col.key]?.value || ''}
                                            onChange={(e) => this.updateFilterValue(col.key, e.target.value)}
                                            style={{ fontSize: '10px', padding: '2px 4px' }}
                                        />
                                    ) : (col.dataType === 'number' || col.dataType === 'currency' || col.dataType === 'percentage') ? (
                                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                                            <select
                                                className="form-select form-select-sm"
                                                value={this.state.columnFilters[col.key]?.condition || '='}
                                                onChange={(e) => this.updateFilterCondition(col.key, e.target.value)}
                                                style={{
                                                    fontSize: '10px',
                                                    padding: '2px',
                                                    width: '25px',
                                                    appearance: 'none',
                                                    WebkitAppearance: 'none',
                                                    MozAppearance: 'none',
                                                    backgroundImage: 'none'
                                                }}
                                            >
                                                <option value="=">=</option>
                                                <option value="<">&lt;</option>
                                                <option value=">">&gt;</option>
                                                <option value="<=">&lt;=</option>
                                                <option value=">=">&gt;=</option>
                                            </select>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                placeholder="Value"
                                                value={this.state.columnFilters[col.key]?.value || ''}
                                                onChange={(e) => this.updateFilterValue(col.key, e.target.value)}
                                                style={{ fontSize: '10px', padding: '2px 4px', width: '100%', minWidth: '30px' }}
                                            />
                                        </div>
                                    ) : null}
                                </th>
                            ))
                    ) : (
                        // Show filter for first visible column when collapsed
                        this.state.attributeColumns.filter(col => col.isVisible !== false).length > 0 && (() => {
                            const firstVisibleCol = this.state.attributeColumns.filter(col => col.isVisible !== false)[0];
                            return (
                                <th
                                    key="filter-attr-collapsed"
                                    className={`text-${this.getAlignment(firstVisibleCol)}`}
                                >
                                    {firstVisibleCol.dataType === 'string' ? (
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder={`Search ${firstVisibleCol.header}`}
                                            value={this.state.columnFilters[firstVisibleCol.key]?.value || ''}
                                            onChange={(e) => this.updateFilterValue(firstVisibleCol.key, e.target.value)}
                                            style={{ fontSize: '10px', padding: '2px 4px' }}
                                        />
                                    ) : (firstVisibleCol.dataType === 'number' || firstVisibleCol.dataType === 'currency' || firstVisibleCol.dataType === 'percentage') ? (
                                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                                            {firstVisibleCol.dataType === 'currency' &&
                                                <span style={{ fontSize: '10px' }}>$</span>}
                                            <select
                                                className="form-select form-select-sm"
                                                value={this.state.columnFilters[firstVisibleCol.key]?.condition || '='}
                                                onChange={(e) => this.updateFilterCondition(firstVisibleCol.key, e.target.value)}
                                                style={{
                                                    fontSize: '10px',
                                                    padding: '2px',
                                                    width: '25px',
                                                    appearance: 'none',
                                                    WebkitAppearance: 'none',
                                                    MozAppearance: 'none',
                                                    backgroundImage: 'none'
                                                }}
                                            >
                                                <option value="=">=</option>
                                                <option value="<">&lt;</option>
                                                <option value=">">&gt;</option>
                                                <option value="<=">&lt;=</option>
                                                <option value=">=">&gt;=</option>
                                            </select>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                placeholder="Value"
                                                value={this.state.columnFilters[firstVisibleCol.key]?.value || ''}
                                                onChange={(e) => this.updateFilterValue(firstVisibleCol.key, e.target.value)}
                                                style={{ fontSize: '10px', padding: '2px 4px', width: '100%', minWidth: '30px' }}
                                            />
                                            {firstVisibleCol.dataType === 'percentage' &&
                                                <span style={{ fontSize: '10px' }}>%</span>}
                                        </div>
                                    ) : null}
                                </th>
                            );
                        })()
                    )}

                    {/* Filter inputs for past quarter weeks */}
                    {pastQuarters.map((quarter, qIdx) => {
                        const isExpanded = this.state.expandedQuarters[quarter.name];
                        const displayWeeks = quarter.weekNames || quarter.weeks || [];

                        if (isExpanded) {
                            return displayWeeks.map((weekName, wIdx) => {
                                const filterKey = `${quarter.name}_${weekName}`;
                                return (
                                    <th key={`filter-past-quarter-${qIdx}-week-${wIdx}`} className="text-right">
                                        <div style={{ display: 'flex', gap: '2px' }}>
                                            <select
                                                className="form-select form-select-sm"
                                                value={this.state.columnFilters[filterKey]?.condition || '='}
                                                onChange={(e) => this.updateFilterCondition(filterKey, e.target.value)}
                                                style={{
                                                    fontSize: '10px',
                                                    padding: '2px',
                                                    width: '25px',
                                                    appearance: 'none',
                                                    WebkitAppearance: 'none',
                                                    MozAppearance: 'none',
                                                    backgroundImage: 'none'
                                                }}
                                            >
                                                <option value="=">=</option>
                                                <option value="<">&lt;</option>
                                                <option value=">">&gt;</option>
                                                <option value="<=">&lt;=</option>
                                                <option value=">=">&gt;=</option>
                                            </select>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                placeholder="Value"
                                                value={this.state.columnFilters[filterKey]?.value || ''}
                                                onChange={(e) => this.updateFilterValue(filterKey, e.target.value)}
                                                style={{ fontSize: '10px', padding: '2px 4px', width: '100%', minWidth: '30px' }}
                                            />
                                        </div>
                                    </th>
                                );
                            });
                        } else {
                            const firstWeekName = displayWeeks[0];
                            const filterKey = `${quarter.name}_${firstWeekName}`;
                            return (
                                <th key={`filter-past-quarter-${qIdx}-collapsed`} className="text-right">
                                    <div style={{ display: 'flex', gap: '2px' }}>
                                        <select
                                            className="form-select form-select-sm"
                                            value={this.state.columnFilters[filterKey]?.condition || '='}
                                            onChange={(e) => this.updateFilterCondition(filterKey, e.target.value)}
                                            style={{
                                                fontSize: '10px',
                                                padding: '2px',
                                                width: '25px',
                                                appearance: 'none',
                                                WebkitAppearance: 'none',
                                                MozAppearance: 'none',
                                                backgroundImage: 'none'
                                            }}
                                        >
                                            <option value="=">=</option>
                                            <option value="<">&lt;</option>
                                            <option value=">">&gt;</option>
                                            <option value="<=">&lt;=</option>
                                            <option value=">=">&gt;=</option>
                                        </select>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            placeholder="Value"
                                            value={this.state.columnFilters[filterKey]?.value || ''}
                                            onChange={(e) => this.updateFilterValue(filterKey, e.target.value)}
                                            style={{ fontSize: '10px', padding: '2px 4px', width: '70px' }}
                                        />
                                    </div>
                                </th>
                            );
                        }
                    })}

                    {/* Filter inputs for action columns */}
                    {this.state.cols.map((group, groupIdx) => {
                        if (group.groupKey === 'actions') {
                            return group.columns.map((col, colIdx) => (
                                <th
                                    key={`filter-${groupIdx}-${colIdx}`}
                                    className={`text-${this.getAlignment(col)}`}
                                >
                                    {col.dataType === 'number' ? (
                                        <div style={{ display: 'flex', gap: '2px' }}>
                                            <select
                                                className="form-select form-select-sm"
                                                value={this.state.columnFilters[col.key]?.condition || '='}
                                                onChange={(e) => this.updateFilterCondition(col.key, e.target.value)}
                                                style={{
                                                    fontSize: '10px',
                                                    padding: '2px',
                                                    width: '25px',
                                                    appearance: 'none',
                                                    WebkitAppearance: 'none',
                                                    MozAppearance: 'none',
                                                    backgroundImage: 'none'
                                                }}
                                            >
                                                <option value="=">=</option>
                                                <option value="<">&lt;</option>
                                                <option value=">">&gt;</option>
                                                <option value="<=">&lt;=</option>
                                                <option value=">=">&gt;=</option>
                                            </select>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                placeholder="Value"
                                                value={this.state.columnFilters[col.key]?.value || ''}
                                                onChange={(e) => this.updateFilterValue(col.key, e.target.value)}
                                                style={{ fontSize: '10px', padding: '2px 4px', width: '100%', minWidth: '30px' }}
                                            />
                                        </div>
                                    ) : null}
                                </th>
                            ));
                        }
                        return null;
                    })}

                    {/* Filter inputs for future quarter weeks */}
                    {futureQuarters.map((quarter, qIdx) => {
                        const isExpanded = this.state.expandedQuarters[quarter.name];
                        const displayWeeks = quarter.weekNames || quarter.weeks || [];

                        if (isExpanded) {
                            return displayWeeks.map((weekName, wIdx) => {
                                const filterKey = `${quarter.name}_${weekName}`;
                                return (
                                    <th key={`filter-future-quarter-${qIdx}-week-${wIdx}`} className="text-right">
                                        <div style={{ display: 'flex', gap: '2px' }}>
                                            <select
                                                className="form-select form-select-sm"
                                                value={this.state.columnFilters[filterKey]?.condition || '='}
                                                onChange={(e) => this.updateFilterCondition(filterKey, e.target.value)}
                                                style={{
                                                    fontSize: '10px',
                                                    padding: '2px',
                                                    width: '25px',
                                                    appearance: 'none',
                                                    WebkitAppearance: 'none',
                                                    MozAppearance: 'none',
                                                    backgroundImage: 'none'
                                                }}
                                            >
                                                <option value="=">=</option>
                                                <option value="<">&lt;</option>
                                                <option value=">">&gt;</option>
                                                <option value="<=">&lt;=</option>
                                                <option value=">=">&gt;=</option>
                                            </select>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                placeholder="Value"
                                                value={this.state.columnFilters[filterKey]?.value || ''}
                                                onChange={(e) => this.updateFilterValue(filterKey, e.target.value)}
                                                style={{ fontSize: '10px', padding: '2px 4px', width: '100%', minWidth: '30px' }}
                                            />
                                        </div>
                                    </th>
                                );
                            });
                        } else {
                            const firstWeekName = displayWeeks[0];
                            const filterKey = `${quarter.name}_${firstWeekName}`;
                            return (
                                <th key={`filter-future-quarter-${qIdx}-collapsed`} className="text-right">
                                    <div style={{ display: 'flex', gap: '2px' }}>
                                        <select
                                            className="form-select form-select-sm"
                                            value={this.state.columnFilters[filterKey]?.condition || '='}
                                            onChange={(e) => this.updateFilterCondition(filterKey, e.target.value)}
                                            style={{
                                                fontSize: '10px',
                                                padding: '2px',
                                                width: '25px',
                                                appearance: 'none',
                                                WebkitAppearance: 'none',
                                                MozAppearance: 'none',
                                                backgroundImage: 'none'
                                            }}
                                        >
                                            <option value="=">=</option>
                                            <option value="<">&lt;</option>
                                            <option value=">">&gt;</option>
                                            <option value="<=">&lt;=</option>
                                            <option value=">=">&gt;=</option>
                                        </select>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            placeholder="Value"
                                            value={this.state.columnFilters[filterKey]?.value || ''}
                                            onChange={(e) => this.updateFilterValue(filterKey, e.target.value)}
                                            style={{ fontSize: '10px', padding: '2px 4px', width: '70px' }}
                                        />
                                    </div>
                                </th>
                            );
                        }
                    })}
                </tr>
                </thead>

                <tbody>
                {this.state.rows.length === 0 && !this.state.isLoading ? (
                    <tr>
                        <td colSpan={100} className="text-center py-4">
                            No data available, please try again!
                        </td>
                    </tr>
                ) : null}
                {this.state.rows.map((row, rowIdx) => (
                    <tr
                        key={row.sequence}
                        className={this.state.draggedRowIndex === rowIdx ? 'dragging-row' : ''}
                    >
                        {/* Render fixed columns */}
                        {this.state.cols.map((group, groupIdx) => {
                            if (group.groupKey === 'fixed') {
                                return group.columns.map((col, colIdx) => {
                                    const value = this.getRowValue(row, group.groupKey, col.key);
                                    const cellContent = this.renderCell(col, value, group.groupKey, row, rowIdx);

                                    // Only SKU and SKU Description columns should be draggable
                                    const isDraggable = col.key === 'sku' || col.key === 'skuDescription' || col.key === 'product_code' || col.key === 'product_description';

                                    return (
                                        <td
                                            key={`${groupIdx}-${colIdx}`}
                                            className={`text-${!isDraggable ? this.getAlignment(col) : 'left'} ${col.dataType !== 'icon' ? 'table-data-text' : ''} ${this.getStickyClass(col, group.groupKey, false)}`}
                                            style={{ cursor: isDraggable ? 'move' : 'default' }}
                                            draggable={isDraggable}
                                            onDragStart={isDraggable ? (e) => this.handleDragStart(e, rowIdx) : undefined}
                                            onDragOver={isDraggable ? (e) => this.handleDragOver(e, rowIdx) : undefined}
                                            onDragEnd={isDraggable ? this.handleDragEnd : undefined}
                                        >
                                            {cellContent}
                                        </td>
                                    );
                                });
                            }
                            return null;
                        })}

                        {/* Render attribute columns */}
                        {this.state.isAttributesExpanded ? (
                            this.state.attributeColumns
                                .filter(col => col.isVisible !== false)
                                .map((col, colIdx) => {
                                    const value = this.getRowValue(row, 'attributes', col.key);
                                    const cellContent = this.renderCell(col, value, 'attributes', row, rowIdx);

                                    return (
                                        <td
                                            key={`attr-${colIdx}`}
                                            className={`text-${this.getAlignment(col)} table-data-text`}
                                            style={{
                                                maxWidth: `${this.state.columnWidths[col.key] || 60}px`,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {cellContent}
                                        </td>
                                    );
                                })
                        ) : (
                            // Show only first visible column when collapsed
                            this.state.attributeColumns.filter(col => col.isVisible !== false).length > 0 && (() => {
                                const firstVisibleCol = this.state.attributeColumns.filter(col => col.isVisible !== false)[0];
                                return (
                                    <td
                                        key="attr-collapsed"
                                        className={`text-${this.getAlignment(firstVisibleCol)} table-data-text`}
                                        style={{
                                            maxWidth: `${this.state.columnWidths[firstVisibleCol.key] || 60}px`,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {this.renderCell(
                                            firstVisibleCol,
                                            this.getRowValue(row, 'attributes', firstVisibleCol.key),
                                            'attributes',
                                            row,
                                            rowIdx
                                        )}
                                    </td>
                                );
                            })()
                        )}

                        {/* Render PAST quarter week cells */}
                        {pastQuarters.map((quarter, qIdx) => {
                            const isExpanded = this.state.expandedQuarters[quarter.name];

                            if (isExpanded) {
                                return (quarter.weekNames || []).map((weekName, wIdx) => {
                                    const cellContent = this.renderWeekCell(row, rowIdx, quarter.name, weekName);
                                    const cellStyles = this.getCellStyles(quarter.name, weekName, row);

                                    return (
                                        <td
                                            key={`past-quarter-${qIdx}-week-${wIdx}`}
                                            className="text-right table-data-text"
                                            style={cellStyles}
                                        >
                                            {cellContent}
                                        </td>
                                    );
                                });
                            } else {
                                // Show only first week when collapsed
                                const firstWeekName = (quarter.weekNames || [])[0];
                                const cellContent = this.renderWeekCell(row, rowIdx, quarter.name, firstWeekName);
                                const cellStyles = this.getCellStyles(quarter.name, firstWeekName, row);

                                return (
                                    <td
                                        key={`past-quarter-${qIdx}-collapsed`}
                                        className="text-right table-data-text"
                                        style={cellStyles}
                                    >
                                        {cellContent}
                                    </td>
                                );
                            }
                        })}

                        {/* Render action columns */}
                        {this.state.cols.map((group, groupIdx) => {
                            if (group.groupKey === 'actions') {
                                return group.columns.map((col, colIdx) => {
                                    const value = this.getRowValue(row, group.groupKey, col.key);
                                    const cellContent = this.renderCell(col, value, group.groupKey, row, rowIdx);

                                    return (
                                        <td
                                            key={`${groupIdx}-${colIdx}`}
                                            className={`text-${this.getAlignment(col)} table-data-text`}
                                            style={{
                                                maxWidth: '60px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {cellContent}
                                        </td>
                                    );
                                });
                            }
                            return null;
                        })}

                        {/* Render FUTURE quarter week cells */}
                        {futureQuarters.map((quarter, qIdx) => {
                            const isExpanded = this.state.expandedQuarters[quarter.name];

                            if (isExpanded) {
                                return (quarter.weekNames || []).map((weekName, wIdx) => {
                                    const cellContent = this.renderWeekCell(row, rowIdx, quarter.name, weekName);
                                    const cellStyles = this.getCellStyles(quarter.name, weekName, row);

                                    return (
                                        <td
                                            key={`future-quarter-${qIdx}-week-${wIdx}`}
                                            className="text-right table-data-text"
                                            style={cellStyles}
                                        >
                                            {cellContent}
                                        </td>
                                    );
                                });
                            } else {
                                // Show only first week when collapsed
                                const firstWeekName = (quarter.weekNames || [])[0];
                                const cellContent = this.renderWeekCell(row, rowIdx, quarter.name, firstWeekName);
                                const cellStyles = this.getCellStyles(quarter.name, firstWeekName, row);

                                return (
                                    <td
                                        key={`future-quarter-${qIdx}-collapsed`}
                                        className="text-right table-data-text"
                                        style={cellStyles}
                                    >
                                        {cellContent}
                                    </td>
                                );
                            }
                        })}
                    </tr>
                ))}
                </tbody>
            </table>
        </div>;

        const selectedRow = this.state.selectedRowForAssignment !== null
            ? this.state.rows[this.state.selectedRowForAssignment]
            : null;

        return (
            <React.Fragment>
                <PageLoader
                    status={this.state.isLoading}
                />

                {headerSection}
                {bodySection}

                {(this.state.hasUnsavedEdits) && (
                    <ActionCard
                        onCancel={this.handleCancel}
                        onSaveAndFreeze={this.handleSaveAndFreeze}
                        isSaveAndFreezeDisabled={false}
                    />
                )}

                {selectedRow && (
                    <SKUAssignmentModal
                        isOpen={this.state.isSKUAssignmentModalOpen}
                        toggle={this.toggleSKUAssignmentModal}
                        currentSku={selectedRow.sku || ''}
                        predecessorSku={selectedRow.attributes?.prod_attribute2 || ''}
                        onSave={this.handleSKUAssignmentSave}
                    />
                )}

                <SuccessModal
                    isOpen={this.state.isSuccessModalOpen}
                    toggle={this.toggleSuccessModal}
                    message={this.state.successModalMessage}
                />

                <ColorLegendModal
                    isOpen={this.state.isColorLegendModalOpen}
                    toggle={this.toggleColorLegendModal}
                />
            </React.Fragment>
        );
    }
}
